/**
 * Milano Wigs — PDP Personalization Panel v2
 * Palo Alto 9.1 + SC Options App Embed
 * v2026-06-03a
 *
 * CHANGE (v2026-06-03a): Production hardening pass. Adds a final
 * form-payload preparation hook for cart submits, reduces no-promo Discount
 * Ninja observer work, and keeps defensive writes scoped/idempotent.
 *
 * CHANGE (v2026-06-02c): Gallery labeler now runs through one rAF queue and
 * avoids extra full-gallery scans during rapid variant/media events. Paired
 * with variant-media-gallery-code v4.10 for lower mobile INP.
 *
 * CHANGE (v2026-05-21b): Bootstrap consolidation + variant image preload.
 * - Unified bootstrap registry (mwAddBootstrapTask / MILANO_addBootstrap):
 *   replaces 6 separate 200ms setIntervals with one shared interval. Each
 *   task is { fn, stopOnSuccess, done, attempts } with try/catch isolation.
 *   Same semantics per task, fewer event-loop entries. Exposed via
 *   window.MILANO_addBootstrap so wizard.js's help-links bootstrap and
 *   autoSelectCapSize / reorderForGrace also route through it.
 * - preloadVariantImagesForGrace: extracts Length tile image URLs,
 *   upgrades to _800x / width=800, injects <link rel=preload as=image
 *   fetchpriority=low> into <head>. Deferred via requestIdleCallback
 *   (1.2s setTimeout fallback). Length tile click → instant gallery swap
 *   instead of network round-trip. Idempotent via [data-mw-variant-preload].
 *
 * CHANGE (v2026-05-21): Eliminate 1-line→2-line label flicker on variant change.
 * - graceObserver: removed 60ms debounce on splitGraceLengthLabels. The
 *   MutationObserver callback runs as a microtask, before the next paint.
 *   Splitting synchronously means the user never sees the unwrapped
 *   "12\" Halle" state — the .mw-len-num + .mw-len-name spans are in
 *   place before the browser paints, eliminating the layout flicker.
 *   Function remains idempotent (data-mw-split guard) — duplicate
 *   mutations within a single Globo render coalesce harmlessly.
 *
 * CHANGE (v2026-05-19i): Critical Grace Length fixes.
 * - FIX pill placement: prior querySelector('.radio__legend__label, .radio__legend')
 *   returned <legend> first (tree order — parent precedes child). Pill went
 *   into <legend>'s block-level flow instead of the inner span, so it
 *   rendered on its own line below "Length" and CSS flex rules for the
 *   span never applied. Now uses priority queries: span first, legend
 *   fallback only if span is absent.
 * - NEW ensureLengthLegendLayout: wraps the raw "Length" text in a stable
 *   .mw-len-text span (so pill can sit next to it as a sibling flex item),
 *   creates the .radio__legend__label span if the theme omits it, and
 *   applies safety-net inline flex styles.
 * - FIX observer scope: prior code observed `document.querySelector('.radio__buttons')`
 *   which returned the FIRST one (Cap size on Grace) — not the Length one.
 *   Re-scoped to a stable ancestor (variant-selects / .product__selectors)
 *   with subtree:true + relevance filter that excludes our own injected
 *   elements (no feedback loops).
 * - 3 additional pill updates (0/250/600ms) after radio change to cover
 *   Globo's post-click re-render that can wipe the just-set pill.
 *
 * CHANGE (v2026-05-19h): Grace Length consistency across all PDP actions.
 * - FIX latent bug: prior MutationObserver was created but never attached
 *   (no .observe() call). Only the boot retry interval + delegated change
 *   listener were doing real work. Observer now properly bound.
 * - attachGraceObserver(): module-scoped + rebindable. When Globo replaces
 *   the entire .radio__buttons element on color variant change, we
 *   disconnect the now-detached observer and rebind to the new container.
 * - readLengthLabel(): split-aware label extraction. After splitGraceLengthLabels
 *   wraps "19\" NICOLE" into .mw-len-num + .mw-len-name spans, textContent
 *   becomes "19\"NICOLE" (no space). Now reads the two spans separately and
 *   joins with a space — pill text is always correct mid-race.
 * - window.MILANO_refreshGraceLength: public hook called by wizard.js's
 *   watchVariantChanges after each color-variant re-render. Re-splits
 *   labels, refreshes the pill, and rebinds the observer.
 * - Skip pill innerHTML write if unchanged — avoids spurious mutations.
 *
 * CHANGE (v2026-05-19g): Grace Length pill repositioning.
 * - updateLengthSelectedPill(): pill now inserted BEFORE the .mw-lace-help
 *   button so the legend reads [Length] [Selected pill] ........... [See lengths].
 *   The CSS pushes "See lengths" to the right via margin-left:auto, matching
 *   the Size guide pattern on Cap size for visual consistency.
 * - Position is re-asserted on every call (covers pages cached with prior
 *   after-button placement, and re-anchors after MutationObserver re-renders).
 *
 * CHANGE (v2026-05-16): Inventory copy softener.
 * - NEW: softenInventoryCopy() walks .variant__countdown--low child nodes
 *   and replaces "Hurry! Low inventory" → "Limited availability" on text
 *   nodes only (preserves dot icon child). Same bootstrap pattern as
 *   splitProductTitle — retry up to 10× over ~2s in case the inventory
 *   block paints late. Idempotent via data-mw-softened="1" flag.
 *   Rationale: pressure-style framing ("Hurry!") erodes trust at Milano's
 *   $1,450+ price point; calm "Limited availability" signals scarcity
 *   without selling urgency.
 *
 * CHANGE (v2026-05-15): Client-side product title split.
 * - NEW: splitProductTitle() runs on DOMContentLoaded. Finds .product__title,
 *   splits on " #" so the wig name renders as .product__title__main and the
 *   color variant ("#12 Warm Light Brown") renders as .product__title__variant
 *   on a smaller secondary line. Replaces the abandoned Liquid-based approach
 *   in sections/product.liquid (which had parser issues inside the
 *   {%- liquid -%} block). Zero theme-file risk; idempotent (no-op if already
 *   split or if title has no " #" separator). CSS for the two spans already
 *   ships in custom.css.
 *
 * CHANGE (v2026-05-14): Splitit thousand-separator formatting.
 * - FIX: per-month values like $2066.67 now render as "$2,066.67". Both
 *   restoration and sync paths use toLocaleString('en-US', { ... }) for
 *   consistent comma formatting. No external formatter dependency.
 *
 * CHANGE (v2026-05-13): Splitit icon-flash fix.
 * - FIX: Cached innerHTML restoration rebuilds <img> DOM nodes, but the
 *   browser briefly renders them BEFORE the resource is ready to paint —
 *   showing a sub-blink-of-an-eye empty/broken-image frame. Two fixes:
 *     (a) On first cache, extract all <img src> URLs and inject
 *         <link rel="preload" as="image"> for each. Keeps the browser's
 *         HTTP cache hot so every subsequent rebuild has bytes ready.
 *     (b) On every restore, find any img that hasn't fully decoded and
 *         set opacity:0 with a 120ms fade; load/error events flip
 *         opacity back. Opacity is compositor-only — no layout shift
 *         between hidden and revealed states.
 *
 * CHANGE (v2026-05-26): KLAVIYO DEPENDENCY DOCUMENTED
 * - NOT a code change. Documentation only, added after discovering Memorial
 *   Day Sale (5/22\u20135/25) emails captured MSRP instead of discounted price.
 *
 * \u26A0\uFE0F  KLAVIYO DEPENDENCY \u2014 IMPORTANT FOR FUTURE SALES
 *
 *   The v2026-05-12 teardown below removed the client-side sale display layer
 *   (injectSaleBanner, syncProductPrice, storeDNRatio, fixDrawerPrices IIFE).
 *   What was NOT understood at teardown time: Klaviyo's web pixel (_learnq)
 *   reads product prices from the page DOM at event-fire time for these
 *   metrics:
 *     - "Viewed Product"
 *     - "Added to Cart"
 *     - Browse abandonment (derived from Viewed Product)
 *     - Cart abandonment (derived from Added to Cart)
 *
 *   During Spring Sale and Mother's Day, the sale display layer was injecting
 *   strikethrough/discounted prices into the PDP and cart drawer BEFORE
 *   Klaviyo's pixel captured them. Klaviyo stored discounted values in its
 *   event Price properties, which then flowed into abandonment email
 *   templates and showed the correct sale prices.
 *
 *   With the teardown, the PDP and cart drawer now render only Shopify's
 *   native MSRP at the moment Klaviyo fires. Klaviyo captures MSRP. Emails
 *   show MSRP. Confirmed during Memorial Day Sale.
 *
 *   BEFORE NEXT SALE:
 *     1. Restore injectSaleBanner / syncProductPrice / storeDNRatio /
 *        fixDrawerPrices from git history (commit immediately before
 *        v2026-05-12 teardown).
 *     2. Rebuild with the two bug fixes the teardown was meant to address:
 *        (a) Case 2 ratio leak onto accessories (storeDNRatio scope)
 *        (b) Cart checkout button showing undiscounted total
 *     3. Verify in staging before launch: Klaviyo \u2192 Analytics \u2192 Metrics \u2192
 *        "Viewed Product" \u2192 recent event from a sale-eligible PDP \u2192 confirm
 *        the Price property reflects the discounted value, not MSRP.
 *     4. Spot-check abandonment email previews in Klaviyo template editor
 *        against a fresh test session.
 *
 *   Memorial Day already ended (5/25) so no restoration needed for that
 *   cycle. This comment exists so the dependency doesn't get lost again
 *   when the next sale layer is rebuilt.
 *
 * CHANGE (v2026-05-12): SALE INFRASTRUCTURE TEARDOWN (Mother's Day)
 * - REMOVED: injectSaleBanner() + its setTimeout call site. PDP promotional
 *   banner no longer renders. Mother's Day copy is gone.
 * - REMOVED: syncProductPrice() + the .mw-pdp-strike injection + the inline
 *   CSS rules that styled it. PDP product price no longer renders client-side
 *   strikethrough; DN handles its own price display when a future sale runs.
 * - REMOVED: storeDNRatio() + its onPriceChange call + post-init setTimeout.
 *   No code writes mw_dn_ratio to sessionStorage now.
 * - REMOVED: computeFinalPrice() sessionStorage ratio read. The dnDiscount
 *   flat fallback path remains (defensive — works if DN is enabled on PDP
 *   with direct discounts). Breadcrumb comment marks the spot.
 * - REMOVED: cart drawer fixDrawerPrices() IIFE (entire 130-line block).
 *   Cart drawer reverts to Palo Alto's native rendering — no client-side
 *   strikethrough. Mootly resolves the two known bugs from the May 8 sale:
 *     (a) Case 2 incorrectly applied ratio to accessories — gone.
 *     (b) Cart checkout button showed undiscounted total — moot without
 *         a discount to display.
 * - For a future sale, restore from git history (commit before this change)
 *   or rebuild against whatever pricing app is in place at that time.
 *
 * CHANGE (v2026-05-11): FORM ATTRIBUTE STRINGIFICATION FIX
 * - FIX: getProductForm() returns a Shopify product <form> that contains
 *   <input name="id" value="{{ variant.id }}">. Per HTML spec, named form
 *   children become properties of the form element, so `form.id` returns
 *   the hidden variant-id INPUT, not the form's id attribute string.
 *   Stringifying that input to setAttribute('form', ...) produced
 *   form="[object HTMLInputElement]", which:
 *     1. Detached relocated SC Options inputs from form submission
 *        (attribute pointed at a non-existent form id)
 *     2. Triggered downstream pixel validation errors (Klaviyo, Angler)
 *        when those scripts tried to read root.form.id as a primitive.
 * - Fix: read the id via getAttribute('id'), which always returns the
 *   attribute string and bypasses the named-element collision.
 *
 * CHANGE (2026-07-13): retired generic sale and OOS-to-preorder decoration.
 * - Promotion labels now require Discount Ninja confirmation.
 * - Preorder is rendered only from the explicit Liquid product signal.
 *
 * CHANGE (v2026-05-07b): PDP PRICE + CART DRAWER PERSISTENCE + RATIO FIX
 * - ADDED: syncProductPrice() — renders strikethrough on PDP product price
 *   when mw_dn_ratio exists in sessionStorage. Sibling .mw-pdp-strike
 *   element, hides original via class. CSS order:-1 positions sale price
 *   before wizard's (+$X services) in flex layouts.
 * - ADDED: storeDNRatio() — stores DN discount ratio in sessionStorage.
 *   Guard: only touches sessionStorage when DN DOM elements are present,
 *   preserving ratio set from collection pages when DN is off on PDP.
 * - FIX: computeFinalPrice() uses stored ratio for accurate percentage
 *   discount on SC Options total including add-ons.
 * - FIX: Cart drawer strikethrough persists across re-renders. Added
 *   theme:cart:change listener and drawer-open body class observer.
 *
 * CHANGE (v2026-05-07a): DN CART DRAWER PRICE CORRUPTION FIX
 * - REMOVED: updateDNDisplay(), updateDNDataAttrs(), findDNStrikeTextEl(),
 *   getDNOriginalPriceCents(), getDNDiscountRatio(), resetDNVariantBase(),
 *   weAreWritingDN flag, _dnVariantBaseCents, _dnDiscountRatio variables.
 * - These functions wrote SC Options add-on-inflated prices into DN's DOM
 *   elements, causing a MutationObserver write-war loop. DN then mapped
 *   wrong prices to cart drawer rows, displaying wig prices on accessories.
 * - computeFinalPrice() simplified: uses flat DN discount subtraction
 *   instead of ratio-based math. Cart bar still shows correct final price.
 * - ATC button price HIDDEN via injected CSS — eliminates the DN vs SC
 *   Options price conflict on the button entirely. Price shown on product
 *   form (DN manages), cart bar (our sync), wizard review card, and cart
 *   drawer (native Liquid). findATCTargets() and ATC writing removed from
 *   doSyncFast() — no DOM writes to fight over.
 * - DN MutationObserver simplified: removed weAreWritingDN guard and
 *   resetDNVariantBase() call. Observer now only invalidates cache and
 *   triggers cooldown on DN re-renders.
 * - Paired with cart-line-items.liquid change: DN's documented per-item hooks
 *   (data-la-dn-cart-item-row/key/item-price/line-price/text) are restored and
 *   the separate theme-side cart price writer remains disabled. DN owns the
 *   dynamic cart price; SC Options properties remain visible as line-item data.
 *
 * CHANGE (v2026-04-01a): REMOVED BOOST QUICK VIEW CODE
 * - Deleted Boost SD V3 quick view IIFE (former lines 2001-2328).
 *   Globo SPF replaces Boost for collection page filtering/search.
 *   Quick view PDP redirect for wigs/toppers ($150+) now handled
 *   by a lightweight click listener in theme.liquid.
 * - Cart Drawer strikethrough IIFE retained (runs on all pages).
 *
 * FIX (v2026-03-27a): SHOP THIS LOOK / SIMPLIFIED OPTION SET SUPPORT
 * - Option sets without a "Personalize It" toggle (e.g., "Shop This Look -
 *   Divine", "Shop This Look - Silk") had their non-cap-size options
 *   (Secure Method, Add Ear Pieces) classified as personalization options
 *   and placed into the accordion panel. Without a toggle, the panel stays
 *   display:none forever — options silently disappear from the PDP.
 * - Added defensive fallback in buildPanel(): if no "Personalize It" SC
 *   option exists AND no .personalize-toggle Liquid snippet is on the page,
 *   ALL remaining options are promoted to always-visible so they render
 *   directly on the PDP. Products with a toggle (e.g., full Divine/Ponytail
 *   sets with 10 options) keep their accordion behavior unchanged.
 *
 * FIX (v2026-03-16a): Quick view enhancement uses price threshold ($150).
 *   Wigs/toppers ($200+) get full enhancement.
 *   Accessories (under $150) keep default Boost quick view.
 *
 * FIX (v2025-03-10b): DOUBLE-INIT GUARD + DEAD CODE CLEANUP
 * - syncStarted was declared inside syncATCPrice() as a local variable,
 *   making the double-call guard ineffective. syncATCPrice() is called
 *   twice on every PDP load (setTimeout 3500ms + waitForBoldOptions path),
 *   producing two concurrent RAF loops and two sets of MutationObservers.
 *   Fix: hoisted _syncStarted to module scope; reset in shopify:section:load
 *   so sync restarts correctly after theme editor section reloads.
 * - Removed unused local variable lastComputedDisplay (declared, never read).
 * - Fixed indentation on theme:cart:added listener (cosmetic).
 *
 * FIX (v2025-03-10a): ATC SAFETY NET + PERF + OVERFLOW GUARD
 * - PERF: Wraps heavy PDP-only init (polling, RAF loop, MutationObservers)
 *   in isPDP guard. Non-PDP pages skip 20s of polling + perpetual RAF.
 *   Quick View and Cart Drawer features still run globally.
 * - ATC SAFETY NET: SC Options hides the original ATC button (bold_hidden,
 *   display:none) and shows a bold_clone copy. If the clone's SC Options
 *   event handler hasn't wired up yet (slow mobile JS), tapping ATC does
 *   nothing. Safety net: a delegated click listener on .bold_clone buttons
 *   sets a 3-second timeout. If theme:cart:added doesn't fire in that
 *   window, it forces the form submit as a fallback.
 * - OVERFLOW GUARD: showSecurePopup sets body overflow:hidden which can
 *   get stuck on mobile if backdrop close fails. Added 15-second auto-
 *   clear failsafe that restores overflow if popup is still open.
 *
 * HOTFIX (v2025-03-06a-hotfix): CART DRAWER AJAX STRIKETHROUGH
 * - Palo Alto wraps drawer in <template>; content not in live DOM on load.
 *   MutationObserver approach failed (nothing to observe at init time).
 * - Fix: Listen for Palo Alto's "theme:cart:added" event (fires after
 *   build() renders items into live DOM), fetch /cart.json, rewrite
 *   .cart__item__price with <ins>/<del> where original_price > final_price.
 * - Also removed data-la-dn-cart-item-row from cart-line-items.liquid to
 *   prevent DN from overwriting per-item prices with base-variant-only values.
 * - Existing Custom CSS (.cart__item__price ins/del) styles the output.
 *
 * FIX (v2025-03-06a): PERCENTAGE DISCOUNT RATIO FOR SC OPTIONS
 * - (Superseded by v2026-05-07a — ratio math removed, flat subtraction used)
 *
 * FIX (v2025-02-27c): DN DISCOUNT DETECTION — CONFIRMED APPROACH
 * - DN's la:dn:product:discount:calculated event does NOT fire in Dynamic
 *   Pricing mode (confirmed via live testing 2026-03-03).
 * - getDNDiscountCents() parses DN's rendered DOM elements using DN-specific
 *   classes confirmed present in live DOM (.limoniapps-discountninja-
 *   strikethrough, -shortcode-discounted_price). Zero false positives —
 *   Palo Alto's native compare-at uses <s data-compare-price>.
 * - Tries data attributes first (exact cents), falls back to text parsing.
 * - Cached 800ms to avoid redundant DOM reads per sync cycle.
 * - Removed unused API event listener (no IIFE-scoped variables needed).
 *
 * FIX (v2025-02-27b): DN DYNAMIC PRICING INTEGRATION
 * - DN switched from "Embedded Tags" mode (CSS classes) to "Dynamic Pricing"
 *   mode (data-la-dn-* data attributes). Updated DN observer to watch
 *   [data-la-dn-price] container (fallback to old class).
 * - Quick View: adds data-la-dn-price on price element for DN targeting.
 * - DN element finders (getDNDiscountCents, findDNSaleTextEl)
 *   use ONLY DN-specific classes/attributes — never generic <s>/<del> parsing,
 *   which would false-positive match Palo Alto's native compare-at price markup.
 *
 * FIX (v2025-02-27a): DN DYNAMIC PRICING COMPATIBILITY
 * - ATC + cart bar sync is EVENT-DRIVEN when no add-ons are selected
 *   (scTotalCents=0) — DN owns ATC entirely, no flicker.
 * - When SC Options IS active (add-ons selected), RAF loop enforces
 *   our computed price at 200ms because DN Dynamic Pricing continuously
 *   overwrites ATC with base discounted price (ignoring add-on costs).
 *   Checks actual DOM text, not cached value, to detect DN overwrites.
 * - When SC Options is not active (scTotalCents=0), ATC is never touched —
 *   DN owns the ATC button entirely for strikethrough pricing.
 * - RAF loop also handles BNPL monitoring + Splitit recovery (500ms).
 * - No initial doSyncFast() on startup — DN renders first.
 * - Quick view: uses DN's official API (data-la-dn-product-handle +
 *   data-la-dn-product-quickview + la:dn:collection:updated event).
 *
 * FIX (v2025-02-25b): DN + SC OPTIONS PRICE UNIFICATION
 * - DN hides original [data-product-price] inside display:none wrapper.
 *   SC Options updates the hidden element; visible DN price never reflects
 *   add-on costs. ATC/cart-bar/BNPL all sync to wrong (un-discounted) total.
 * - New approach: computeFinalPrice() reads SC Options total (variant+addons)
 *   from the hidden element, subtracts DN discount (read from DN data attrs),
 *   produces the true customer-facing price.
 * - All downstream sync (ATC buttons, cart bar, Splitit, Klarna) uses the
 *   computed final price.
 * - DN discount auto-updates on variant change (DN re-renders, RAF re-reads).
 *
 * FIX (v2025-02-25a): DISCOUNT NINJA PDP SUPPORT
 * - findSource: When SC Options is not present, checks for DN's discounted
 *   price element before falling back to regular Palo Alto price.
 *   SC Options still takes priority when present.
 *
 * FIX (v2025-02-16c): BNPL SYNC REWORK + ATC CLONE + UNIVERSAL SYNC
 * - Splitit: Use official API splitit.ui.refresh() per SDK docs. Cache HTML,
 *   restore when Palo Alto section rendering destroys container.
 * - Klarna: Attribute-only update (no refresh-placements). RAF loop keeps
 *   overriding when Klarna's app block reverts on variant change.
 * - ATC price: Target ALL [data-product-price] in ATC area, not just first.
 *   SC Options bold_clone button now gets synced.
 * - syncATCPrice always starts (even without SC Options) for BNPL sync.
 * - findSource falls back to regular price element without SC Options.
 *
 * FIX (v2025-02-16a): FORM CONTAINMENT
 * - .personalize-toggle renders in Palo Alto's "title" block, OUTSIDE the
 *   product <form>. buildPanel() used toggle as DOM anchor → moved all
 *   SC Options inputs outside the form → properties[] never submitted.
 * - Fix: relocate toggle inside the form before using it as anchor.
 * - Safety net: stamp form="<formId>" on all moved inputs.
 *
 *
 * CHANGE (v2026-05-26c): Cap-size + Splitit stability pass.
 * - Splitit fallback is now a persistent owned row under the product price.
 *   It renders from the regular product price on first sync, then updates from
 *   SC Options totals as add-ons change. It no longer waits for the native
 *   Splitit app container to be absent; empty native containers no longer block
 *   the fallback.
 * - Price sync starts shortly after DOM ready instead of waiting 3.5s, with
 *   the old delayed call retained as a guarded fallback.
 * Prior changes (v2025-02-11a):
 * - Hides .bold_option_title inside accordion rows (prevents double title)
 * - Re-extracts option name when conditional options become visible
 * - Hides empty rows (conditional options with no visible form elements)
 * - Moves always-visible options ABOVE the personalize toggle
 * - Styles always-visible options to match Shopify variant selectors
 */

(function() {
  'use strict';

  if (window.MilanoPersonalizeInit) return;
  window.MilanoPersonalizeInit = true;

  var VERSION = 'v2026-06-03a-production-hardening';
  var DEBUG = !!window.MILANO_DEBUG;
  function log() {
    if ((DEBUG || window.MILANO_DEBUG) && window.console && console.log) {
      console.log.apply(console, arguments);
    }
  }
  log('[Milano] personalize-options.js ' + VERSION + ' loaded');

  // =========================================================================
  // PAGE DETECTION — Skip heavy PDP work on non-product pages
  //
  // This script loads sitewide (needed for Quick View on collections and
  // Cart Drawer strikethrough on all pages). But the heavy initialization
  // — 20s of SC Options polling, RAF price sync loop, MutationObservers —
  // should only run on PDP.
  // =========================================================================

  var isPDP = window.location.pathname.indexOf('/products/') !== -1;
  var initialPDPPriceGuardTimer = null;

  // Prevent the server-rendered/native variant amount from winning the first
  // paint while Discount Ninja and SC Options settle. The guard is deliberately
  // fail-open: a resolved price releases it immediately, and the timeout keeps
  // a storefront with a blocked app from ever leaving the price hidden.
  function releaseInitialPDPPriceGuard() {
    if (document.documentElement) document.documentElement.classList.remove('mw-pdp-price-pending');
    if (initialPDPPriceGuardTimer) {
      window.clearTimeout(initialPDPPriceGuardTimer);
      initialPDPPriceGuardTimer = null;
    }
  }

  function startInitialPDPPriceGuard() {
    if (!isPDP || !document.documentElement) return;
    document.documentElement.classList.add('mw-pdp-price-pending');
    if (initialPDPPriceGuardTimer) window.clearTimeout(initialPDPPriceGuardTimer);
    initialPDPPriceGuardTimer = window.setTimeout(releaseInitialPDPPriceGuard, 1600);
  }

  if (isPDP) startInitialPDPPriceGuard();

  if (!isPDP) {
    log('[Milano] Non-PDP page — skipping panel/sync init');
  }

  // =========================================================================
  // BODY CLASS — milano-grace (v2026-05-22b)
  //
  // CRITICAL FIX: previously this class was set only by wizard.js's IIFE.
  // If options.js loaded FIRST, the immediate bootstrap call for
  // labelGraceGallery / preloadVariantImagesForGrace / etc. would see
  // body.milano-grace = false, return TRUE (interpreted as "task done"),
  // and never retry — meaning gallery labels never appeared.
  //
  // Setting the class here in options.js's IIFE (URL-based fallback)
  // guarantees it's present when the first bootstrap tick runs, regardless
  // of script load order. wizard.js also sets it — both are idempotent.
  // =========================================================================
  if (isPDP && /^\/products\/grace/i.test(window.location.pathname)) {
    var _mwAddGraceBody = function() {
      if (document.body) document.body.classList.add('milano-grace');
    };
    if (document.body) {
      _mwAddGraceBody();
    } else {
      document.addEventListener('DOMContentLoaded', _mwAddGraceBody);
    }
  }

  // ===========================================================================
  // UNIFIED BOOTSTRAP REGISTRY (v2026-05-21b)
  //
  // Replaces 6 separate setInterval retry loops (one per bootstrap*) with a
  // single shared 200ms interval that runs all queued tasks per tick.
  //
  // Each task is { fn, stopOnSuccess, done, attempts }. stopOnSuccess=true
  // marks the task done when fn() returns truthy. stopOnSuccess=false runs
  // the task on every tick until the global attempt cap (10) is hit. The
  // interval auto-clears when every task is done or the cap is reached.
  //
  // Benefits: 1 interval instead of 6, fewer event-loop entries during the
  // first 2 seconds of page load, same behavior + semantics per task.
  // Safety: each task isolated in try/catch so one failure can't break the
  // rest of the bootstrap.
  // ===========================================================================
  var mwBootstrapTasks = [];
  var mwBootstrapInterval = null;
  var MW_BOOTSTRAP_MAX_ATTEMPTS = 10;
  var MW_BOOTSTRAP_TICK_MS = 200;

  function mwBootstrapTick() {
    var allDone = true;
    for (var i = 0; i < mwBootstrapTasks.length; i++) {
      var task = mwBootstrapTasks[i];
      if (task.done) continue;
      if (task.attempts >= MW_BOOTSTRAP_MAX_ATTEMPTS) { task.done = true; continue; }
      allDone = false;
      task.attempts++;
      try {
        var result = task.fn();
        if (task.stopOnSuccess && result) task.done = true;
      } catch (err) {
        if (window.MILANO_DEBUG) console.warn('[Milano Bootstrap] task error:', err);
      }
    }
    if (allDone && mwBootstrapInterval) {
      clearInterval(mwBootstrapInterval);
      mwBootstrapInterval = null;
    }
  }

  function mwAddBootstrapTask(fn, stopOnSuccess) {
    var task = { fn: fn, stopOnSuccess: !!stopOnSuccess, done: false, attempts: 0 };
    mwBootstrapTasks.push(task);
    // Run once immediately so success paths don't wait 200ms
    try {
      var result = fn();
      if (task.stopOnSuccess && result) task.done = true;
    } catch (err) {
      if (window.MILANO_DEBUG) console.warn('[Milano Bootstrap] initial task error:', err);
    }
    task.attempts = 1;
    if (!mwBootstrapInterval && !task.done) {
      mwBootstrapInterval = setInterval(mwBootstrapTick, MW_BOOTSTRAP_TICK_MS);
    }
  }
  // Expose for cross-file use (wizard.js's help-links bootstrap)
  window.MILANO_addBootstrap = mwAddBootstrapTask;


  // ===========================================================================
  // VARIANT MUTATION PIPELINE (v2026-05-22c)
  //
  // Consolidates THREE MutationObservers — graceObserver (this file),
  // helpLinkObserver (this file), watchVariantChanges's obs (wizard.js) —
  // into ONE observer that fans out to subscribers. All three previously
  // observed overlapping DOM subtrees (variant-selects / .product__form)
  // with overlapping mutation streams. Consolidation cuts to a single
  // observer + a single shared 50ms debounced batch.
  //
  // Subscriber API:
  //   MILANO_onVariantMutation(fn, { sync, filter })
  //     sync (default false) — true: runs inside observer microtask, before
  //       browser paint. Use for flicker-prevention work that MUST happen
  //       between mutation and paint (e.g., splitGraceLengthLabels,
  //       opacity-hide). Sync subscribers must be fast (< 1ms) and
  //       idempotent.
  //     filter (default null) — predicate(mutation)→bool. Sub only fires
  //       when at least one mutation in the batch matches. Use for
  //       expensive subscribers that should skip irrelevant mutations.
  //
  //   Non-sync subscribers share ONE debounce batch (50ms after last
  //   mutation). They fire in registration order. Re-queuing within the
  //   same batch is a no-op (dedup via indexOf).
  //
  // Global write lock: set true for 150ms after the batch runs. While set,
  // the observer ignores mutations — prevents our DOM writes from
  // re-triggering the pipeline. Matches the prior watchVariantChanges
  // weAreWriting flag behavior.
  //
  // Anchor: .product__form (or .product__selectors fallback). Largest
  // stable parent that contains variant-selects + cap container + lace
  // fieldset. Doesn't get wholesale-replaced by Globo (variant-selects
  // can be), so the observer survives variant changes without re-attach.
  // ===========================================================================
  var mwVariantObserver = null;
  var mwVariantSubs = [];
  var mwVariantBatch = [];
  var mwVariantBatchTimer = null;
  var mwVariantWriteLock = false;
  var MW_VARIANT_BATCH_MS = 50;
  var MW_VARIANT_LOCK_MS = 150;

  function mwVariantSetupObserver() {
    if (mwVariantObserver) return true;
    if (typeof MutationObserver !== 'function') return true; // bail, mark done
    var anchor = document.querySelector('.product__form')
              || document.querySelector('.product__selectors')
              || document.querySelector('variant-selects');
    if (!anchor) return false;

    mwVariantObserver = new MutationObserver(function(mutations) {
      // v2026-05-22c: write lock blocks DEBOUNCED subs only. Sync subs
      // always fire — preserves prior graceObserver behavior where
      // splitGraceLengthLabels kept running during watchVariantChanges'
      // weAreWriting window. Sync subs handle re-entry via their own
      // filter (e.g., isRelevantMutation) or idempotency (data-mw-split,
      // compareDocumentPosition, etc.).
      var locked = mwVariantWriteLock;
      var queuedAny = false;
      for (var i = 0; i < mwVariantSubs.length; i++) {
        var sub = mwVariantSubs[i];
        if (locked && !sub.sync) continue;
        var matches = true;
        if (sub.filter) {
          matches = false;
          for (var m = 0; m < mutations.length; m++) {
            try {
              if (sub.filter(mutations[m])) { matches = true; break; }
            } catch (_) { /* filter error → skip this mutation */ }
          }
        }
        if (!matches) continue;
        if (sub.sync) {
          try { sub.fn(); } catch (e) { /* swallow */ }
        } else {
          if (mwVariantBatch.indexOf(sub) === -1) mwVariantBatch.push(sub);
          queuedAny = true;
        }
      }
      if (queuedAny) {
        if (mwVariantBatchTimer) clearTimeout(mwVariantBatchTimer);
        mwVariantBatchTimer = setTimeout(mwVariantFlushBatch, MW_VARIANT_BATCH_MS);
      }
    });
    mwVariantObserver.observe(anchor, { childList: true, subtree: true });
    return true;
  }

  function mwVariantFlushBatch() {
    mwVariantBatchTimer = null;
    if (mwVariantBatch.length === 0) return;
    mwVariantWriteLock = true;
    var subs = mwVariantBatch.slice();
    mwVariantBatch = [];
    for (var i = 0; i < subs.length; i++) {
      try { subs[i].fn(); } catch (e) {
        log('[Milano] variant sub error:', e);
      }
    }
    setTimeout(function() { mwVariantWriteLock = false; }, MW_VARIANT_LOCK_MS);
  }

  window.MILANO_onVariantMutation = function(fn, opts) {
    if (typeof fn !== 'function') return;
    opts = opts || {};
    mwVariantSubs.push({
      fn: fn,
      sync: !!opts.sync,
      filter: typeof opts.filter === 'function' ? opts.filter : null
    });
    // Lazy-init the observer. If anchor not yet in DOM, the bootstrap
    // registry retries until it is.
    if (!mwVariantSetupObserver()) {
      mwAddBootstrapTask(mwVariantSetupObserver, true);
    }
  };


  // =========================================================================
  // PRODUCT TITLE SPLIT (v2026-05-15)
  //
  // Splits the H1 product title on " #" so the wig name and color variant
  // render as two visually distinct lines:
  //   "26" Divine Luxe Lace Top Wig" (main)
  //   "#12 Warm Light Brown" (variant — smaller, secondary)
  //
  // Implemented client-side after backing out the Liquid-based approach,
  // which proved fragile inside the {%- liquid -%} block in the section
  // file. JS is safer: zero theme-file risk, easy to disable, runs once.
  // =========================================================================

  function splitProductTitle() {
    if (!isPDP) return false;
    var h1 = document.querySelector('.product__title');
    if (!h1) return false;
    if (h1.querySelector('.product__title__main')) return true; // already split

    var host = h1;
    var link = h1.querySelector('a');
    if (link && link.textContent.trim()) host = link;

    var raw = host.textContent.trim();
    if (!raw) return false;

    var main, variant;

    // Strategy 1: explicit " #" separator (used on toppers like
    // "16\" Hairline Topper #Warm Platinum Blonde Wavy"). Highest signal.
    var idx = raw.indexOf(' #');
    if (idx > -1) {
      main = raw.substring(0, idx).trim();
      variant = raw.substring(idx + 1).trim(); // keeps the '#'
    } else {
      // Strategy 2 (v2026-05-16a): natural-language fallback for wigs
      // without a hash separator. Match the LAST occurrence of "wig" or
      // "topper" as a word boundary, split AFTER that word. The leading
      // `.+\s` requires content before the keyword so accessories whose
      // titles START with "Wig" (e.g., "Wig Cap", "Wig Grip") don't match
      // and fall through unsplit.
      //
      // Note: this is intentionally less aggressive than detecting color
      // words (which is fragile — "Wavy" isn't a color, "Long" isn't a
      // color, etc.). The wig/topper marker is reliable because every
      // wig/topper product title contains the type as a word.
      var match = raw.match(/^(.+\s(?:wig|topper))\s+(.+)$/i);
      if (!match) {
        log('[Milano] Title split skipped — no separator or wig/topper marker in: ' + raw);
        return true;
      }
      main = match[1].trim();
      variant = match[2].trim();
    }

    if (!main || !variant) return true;

    host.textContent = '';
    var mainSpan = document.createElement('span');
    mainSpan.className = 'product__title__main';
    mainSpan.textContent = main;
    var variantSpan = document.createElement('span');
    variantSpan.className = 'product__title__variant';
    variantSpan.textContent = variant;
    host.appendChild(mainSpan);
    host.appendChild(variantSpan);
    log('[Milano] Title split: "' + main + '" / "' + variant + '"');
    return true;
  }

  // Run immediately; retry up to 10× over ~2s in case Palo Alto's
  // product-info custom element paints the H1 after our script loads.
  // v2026-05-21b: now routes through the unified bootstrap registry.
  function bootstrapTitleSplit() {
    mwAddBootstrapTask(splitProductTitle, true);
  }

  // ===========================================================================
  // NATIVE SIZE GUIDE RELOCATION (v2026-05-17 / wizard v1.6.15)
  //
  // For products with native Shopify size variants (e.g. toppers with 3 CM /
  // 5 CM, or 5x6"/7x8") the .product__form__size-chart block sits BELOW the
  // "Size" / "Topper Size" legend on its own row. Wig products with SC Options
  // get their size guide injected INTO the cap-size title row via the wizard's
  // reorderCapAndLace(). This function gives native-variant products the same
  // [label][size guide] right-aligned title pattern, so the PDP feels visually
  // coherent across product types.
  //
  // DOM transform:
  //   <fieldset>
  //     <legend>Size</legend>                  ← wrap text in <span>, append size-chart
  //   </fieldset>
  //   <div class="product__form__size-chart">  ← move into legend
  // ===========================================================================

  function relocateNativeSizeGuide() {
    if (!isPDP) return true;
    var sizeChart = document.querySelector('.product__form .product__form__size-chart, .product__form__size-chart');
    if (!sizeChart || sizeChart.classList.contains('mw-size-relocated')) return true;

    // Strategy 1: fieldset > legend with "size" text
    var fieldsets = document.querySelectorAll('.product__form fieldset, .product__form__option, .selector-wrapper');
    for (var i = 0; i < fieldsets.length; i++) {
      var lg = fieldsets[i].querySelector('legend, .form__label, label.product__form__label, .selector__title');
      if (!lg) continue;
      var t = (lg.textContent || '').trim().toLowerCase();
      if (t === 'size' || /\bsize\b/.test(t)) {
        if (lg.closest('.bold_option')) return true;          // SC Options handles this
        if (lg.classList.contains('mw-legend-flex')) return true;

        lg.classList.add('mw-legend-flex');
        var textWrap = document.createElement('span');
        textWrap.className = 'mw-legend-text';
        while (lg.firstChild) textWrap.appendChild(lg.firstChild);
        lg.appendChild(textWrap);

        sizeChart.classList.add('mw-size-relocated');
        lg.appendChild(sizeChart);
        return true;
      }
    }

    // Strategy 2 (v2026-05-17b): some product templates render the size
    // variant label as a plain h-tag or div (no fieldset/legend).
    // Fall back to ANY element on the page with text exactly "Size" or
    // "Topper Size" that's a heading-like element near the size chart.
    var candidates = document.querySelectorAll('.product__form h2, .product__form h3, .product__form h4, .product__form .form__label, .product__form .product__form__label, .product__form [class*="title"]');
    for (var j = 0; j < candidates.length; j++) {
      var ct = (candidates[j].textContent || '').trim().toLowerCase();
      if (ct === 'size' || ct === 'topper size' || /^(topper\s+)?size$/.test(ct)) {
        if (candidates[j].classList.contains('mw-legend-flex')) return true;
        candidates[j].classList.add('mw-legend-flex');
        var tw = document.createElement('span');
        tw.className = 'mw-legend-text';
        while (candidates[j].firstChild) tw.appendChild(candidates[j].firstChild);
        candidates[j].appendChild(tw);
        sizeChart.classList.add('mw-size-relocated');
        candidates[j].appendChild(sizeChart);
        return true;
      }
    }

    return false;
  }

  // ===========================================================================
  // CAP-SIZE SIZE GUIDE RELOCATION (v2026-06-17)
  //
  // Mirrors the wizard's reorderCapAndLace() size-guide injection for the
  // product layouts the wizard doesn't cover:
  //   - "Shop This Look" / minimal option sets (no Personalize toggle), and
  //   - wizard products WITHOUT a Lace Front variant (reorderCapAndLace bails
  //     early when there is no lace).
  // In both cases the native size guide otherwise sits below the price instead
  // of on the "Adjust Cap Size" line. This moves it onto that line so every
  // wig/topper PDP reads consistently. Accessories are skipped. Idempotent.
  // _mwCapGuideEl stashes the relocated trigger so it survives title rebuilds.
  // ===========================================================================
  var _mwCapGuideEl = null;

  function relocateCapSizeGuide() {
    if (!isPDP) return true;

    // Skip accessories — they get no cap-size / size-guide treatment.
    var pinfo = document.querySelector('product-info[data-mw-accessory], [data-mw-accessory]');
    if (pinfo && pinfo.getAttribute('data-mw-accessory') === 'true') return true;
    if ((document.body.className || '').toLowerCase().indexOf('accessor') > -1) return true;

    // Find the cap/base size always-visible option's title row.
    var capTitle = null;
    var groups = document.querySelectorAll('.bold_option.milano-always-visible');
    for (var i = 0; i < groups.length; i++) {
      var t = groups[i].querySelector('.bold_option_title');
      var name = t ? (t.textContent || '').trim().toLowerCase() : '';
      if (!name) name = (groups[i].getAttribute('data-option-name') || '').toLowerCase();
      if (name.indexOf('cap size') > -1 || name.indexOf('base size') > -1) { capTitle = t; break; }
    }
    if (!capTitle) return false; // title not built yet — let the bootstrap retry

    // Already relocated (here, or by the wizard's reorderCapAndLace)? Stash + done.
    var existing = capTitle.querySelector('.mw-size-guide-native, .product__popup-toggle, .product__popup__link');
    if (existing) {
      if (existing.classList.contains('mw-size-guide-native')) _mwCapGuideEl = existing;
      return true;
    }

    // Pull the native size-guide trigger (or restore the stashed one if the
    // native block was already emptied by a prior run / title rebuild).
    var trigger = null;
    var nativeBlock = document.querySelector('.product__form__size-chart');
    if (nativeBlock) trigger = nativeBlock.querySelector('button, a');
    if (!trigger && _mwCapGuideEl) trigger = _mwCapGuideEl;
    if (!trigger) return false; // nothing to relocate yet — retry
    if (!(capTitle.isConnected || capTitle.parentNode)) return false; // stale node — let a retry find a fresh title

    trigger.classList.add('mw-size-guide-native');
    capTitle.appendChild(trigger);
    _mwCapGuideEl = trigger;
    if (nativeBlock) nativeBlock.classList.add('mw-popup-relocated');
    return true;
  }

  // ===========================================================================
  // SIZE GUIDE — FIRST-OPTION FALLBACK (v2026-06-22)
  //
  // Toppers whose only option is "Your Lace Front" (no Size / Cap Size) match
  // neither relocateNativeSizeGuide (no "size" legend) nor relocateCapSizeGuide
  // (no cap/base-size title), so the native size guide drops to its own line
  // below the price. Attach it inline to the FIRST real option title/legend so
  // it reads like the cap-size pattern. Runs AFTER the other two relocators and
  // defers to them: only fires when the guide is still un-relocated AND there is
  // no size-like title for them to claim. Idempotent; skips accessories.
  // ===========================================================================
  function relocateSizeGuideFirstOption() {
    if (!isPDP) return true;
    var pinfo = document.querySelector('product-info[data-mw-accessory], [data-mw-accessory]');
    if (pinfo && pinfo.getAttribute('data-mw-accessory') === 'true') return true;
    if ((document.body.className || '').toLowerCase().indexOf('accessor') > -1) return true;

    var sizeChart = document.querySelector('.product__form .product__form__size-chart, .product__form__size-chart');
    if (!sizeChart) return true;                                        // nothing to move
    if (sizeChart.classList.contains('mw-size-relocated')) return true; // native relocator claimed it
    if (document.querySelector('.mw-size-guide-native')) return true;   // cap / this fn already placed it

    // Defer to the dedicated relocators if a size-like title exists.
    var titleSel = '.product__form .bold_option_title, .product__form legend, .product__form .selector__title, .product__form .form__label';
    var titles = document.querySelectorAll(titleSel);
    for (var i = 0; i < titles.length; i++) {
      var nm = (titles[i].textContent || '').trim().toLowerCase();
      if (nm.indexOf('cap size') > -1 || nm.indexOf('base size') > -1 || /\bsize\b/.test(nm)) return false;
    }

    // First real, visible option title that isn't the size-chart's own label.
    var target = null;
    for (var j = 0; j < titles.length; j++) {
      var el = titles[j];
      if (el.closest('.product__form__size-chart')) continue;
      if (!(el.textContent || '').trim()) continue;
      if (el.offsetParent === null && !el.getClientRects().length) continue;
      target = el; break;
    }
    if (!target) return false;                                          // titles not built yet — retry

    var trigger = sizeChart.querySelector('button, a');
    if (!trigger) return false;

    trigger.classList.add('mw-size-guide-native');
    if (target.tagName === 'LEGEND' && !target.classList.contains('mw-legend-flex')) {
      target.classList.add('mw-legend-flex');
      var tw = document.createElement('span');
      tw.className = 'mw-legend-text';
      while (target.firstChild) tw.appendChild(target.firstChild);
      target.appendChild(tw);
    }
    target.classList.add('mw-size-guide-host');                         // CSS hook for inline layout
    target.appendChild(trigger);
    sizeChart.classList.add('mw-size-relocated');
    _mwCapGuideEl = trigger;
    return true;
  }

  // ===========================================================================
  // PRICE DIVIDER PARITY (v2026-06-22)
  //
  // Wig PDPs show a hairline under the price (the border-top on the always-
  // visible Cap Size row). Native-variant toppers ("Topper Size") have no SC
  // cap-size row, so they had no divider. Tag the price block; custom.css draws
  // the border-bottom and a :has() rule suppresses it whenever the cap-size /
  // first-option border-top is already present, so wigs never get a double line
  // (timing-safe — the CSS, not JS, owns the de-dup). Skips accessories.
  // ===========================================================================
  function ensurePriceDivider() {
    if (!isPDP) return true;
    var pinfo = document.querySelector('product-info[data-mw-accessory], [data-mw-accessory]');
    if (pinfo && pinfo.getAttribute('data-mw-accessory') === 'true') return true;
    if ((document.body.className || '').toLowerCase().indexOf('accessor') > -1) return true;

    var priceBlock = document.querySelector('.product__block.product__price-and-badge');
    if (!priceBlock) return false;                                      // not rendered yet — retry
    priceBlock.classList.add('mw-price-divider');
    return true;
  }

  function bootstrapSizeGuideRelocate() {
    mwAddBootstrapTask(relocateNativeSizeGuide, true);
    mwAddBootstrapTask(relocateCapSizeGuide, true);
    mwAddBootstrapTask(relocateSizeGuideFirstOption, true);
    mwAddBootstrapTask(ensurePriceDivider, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrapSizeGuideRelocate);
  } else {
    bootstrapSizeGuideRelocate();
  }

  // ===========================================================================
  // INVENTORY + PROMOTION STATUS
  //
  // These states are server-rendered in inventory-countdown.liquid and
  // promotion-display.js. Do not infer preorder from ordinary out-of-stock
  // inventory or inject a generic campaign label here: both create false
  // merchandising claims after a variant change.
  // ===========================================================================

  // ===========================================================================
  // GRACE LENGTH LABEL SPLIT (v2026-05-19c / wizard v1.6.21)
  // Splits option titles like "19\" NICOLE" into two spans for visual
  // hierarchy: bigger number, smaller name. Idempotent via data-mw-split.
  // ===========================================================================

  // ===========================================================================
  // GRACE LENGTH SELECTED INDICATOR (v2026-05-19i / wizard v1.6.28)
  // Injects a pill next to the "Length" title showing the currently
  // selected variant (e.g. "19" NICOLE"). Pill positioned right after
  // the "Length" text, before the "Compare lengths" help button (which
  // is pushed right via margin-left:auto, matching the Size guide pattern).
  // ===========================================================================

  // Reads the length label intelligently — handles both pre-split (raw
  // "19\" NICOLE" text) and post-split (.mw-len-num + .mw-len-name spans)
  // states. Without this, clicking a tile then reading textContent during
  // the race window between change-event and re-split gives "19\"NICOLE"
  // (no space) because the appended spans have no whitespace between them.
  function readLengthLabel(optionTitle) {
    if (!optionTitle) return '';
    var num = optionTitle.querySelector('.mw-len-num');
    var name = optionTitle.querySelector('.mw-len-name');
    if (num && name) {
      return ((num.textContent || '').trim() + ' ' + (name.textContent || '').trim()).replace(/\s+/g, ' ').trim();
    }
    return (optionTitle.textContent || '').trim().replace(/\s+/g, ' ');
  }


  function hydrateGraceLengthImages(lengthGroup) {
    // Length tile thumbnails are visible on mobile/tablet and are part of the
    // variant decision UI. Keep them lightweight, but do not leave them at the
    // mercy of lazy-load timing after Palo Alto replaces the option fieldset.
    if (!lengthGroup || !lengthGroup.querySelectorAll) return;
    var imgs = lengthGroup.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      var btn = img.closest('.radio__button');
      var isSelected = !!(btn && btn.querySelector('input:checked'));

      img.classList.add('mw-length-option-img');
      img.setAttribute('decoding', 'async');

      // Do not force every tile eager/high. The main product image owns LCP;
      // selected/above-fold tiles can be eager, while the rest stay lazy/low so
      // rapid mobile variant testing does not create unnecessary image work.
      img.setAttribute('loading', (isSelected || i < 2) ? 'eager' : 'lazy');
      img.setAttribute('fetchpriority', isSelected ? 'auto' : 'low');
      if (!img.getAttribute('width')) img.setAttribute('width', '120');
      if (!img.getAttribute('height')) img.setAttribute('height', '150');
    }
  }

  function findGraceLengthGroup() {
    if (!document.body.classList.contains('milano-grace')) return null;

    // Prefer explicit native Shopify Length inputs. This avoids accidentally
    // classing other 3+ radio groups as the Length grid during wizard or app
    // re-renders, and reduces work during rapid variant changes.
    var checked = document.querySelector('input[data-single-option-selector][name="options[Length]"]:checked');
    var any = checked || document.querySelector('input[data-single-option-selector][name="options[Length]"]');
    if (any) {
      var group = any.closest('.radio__buttons');
      if (group) return group;
    }

    // Fallback for stores/translations where the input name differs: use the
    // first native variant-image fieldset whose legend says Length.
    var fieldsets = document.querySelectorAll('fieldset.radio__fieldset--variant-option-image, fieldset[data-option-position]');
    for (var i = 0; i < fieldsets.length; i++) {
      var text = ((fieldsets[i].querySelector('.radio__legend__label, legend') || {}).textContent || '').toLowerCase();
      if (text.indexOf('length') !== -1) return fieldsets[i].querySelector('.radio__buttons');
    }

    return null;
  }

  function markGraceLengthGrid() {
    if (!document.body.classList.contains('milano-grace')) return null;

    var lengthGroup = findGraceLengthGroup();

    // Remove stale Length classes only from fieldsets previously marked by us.
    var stale = document.querySelectorAll('fieldset.mw-length-fieldset');
    for (var s = 0; s < stale.length; s++) {
      if (!lengthGroup || stale[s] !== lengthGroup.closest('fieldset')) {
        stale[s].classList.remove('mw-length-fieldset');
        var staleGroup = stale[s].querySelector('.radio__buttons.mw-length-grid');
        if (staleGroup) staleGroup.classList.remove('mw-length-grid');
      }
    }

    if (!lengthGroup) return null;

    // Length option order is now owned by Shopify admin for first-paint stability.
    // Do not visually sort/move Length radio nodes in JS; moving native variant inputs
    // during Palo Alto renders can create unnecessary lifecycle risk.
    lengthGroup.classList.add('mw-length-grid');

    var count = lengthGroup.querySelectorAll('.radio__button').length;
    lengthGroup.setAttribute('data-mw-length-count', String(count));
    lengthGroup.classList.toggle('mw-length-many', count >= 7);
    lengthGroup.classList.toggle('mw-length-even', count > 0 && count % 2 === 0);
    lengthGroup.classList.toggle('mw-length-odd', count > 0 && count % 2 === 1);

    var fs = lengthGroup.closest('fieldset');
    if (fs) {
      fs.classList.add('mw-length-fieldset');
      fs.setAttribute('data-mw-length-count', String(count));
      fs.classList.toggle('mw-length-many', count >= 7);
      fs.classList.toggle('mw-length-even', count > 0 && count % 2 === 0);
      fs.classList.toggle('mw-length-odd', count > 0 && count % 2 === 1);
    }
    hydrateGraceLengthImages(lengthGroup);

    return lengthGroup;
  }

  // v2026-05-19i: ensures a stable, flex-ready legend label structure.
  // - Locates the .radio__legend__label span specifically (not the <legend>
  //   wrapper — see Bug 1 below).
  // - Wraps the raw "Length" text in a .mw-len-text span so it becomes a
  //   stable flex item we can position the pill next to.
  // - Sets inline flex styles as a safety net in case CSS doesn't apply.
  //
  // Bug 1 fix: prior code used querySelector('.radio__legend__label, .radio__legend')
  // which returned the <legend> element FIRST (tree order — parent comes
  // before child). The pill then went into <legend> (block-level by browser
  // default) and rendered on its own line below the label text, defeating
  // every flex rule meant for the inner span.
  function ensureLengthLegendLayout(fieldset) {
    if (!fieldset) {
      var lengthGroup = markGraceLengthGrid();
      fieldset = (lengthGroup && lengthGroup.closest('fieldset')) || document.querySelector('fieldset.radio__fieldset--variant-option-image, fieldset.mw-length-fieldset');
    }
    if (!fieldset) return null;

    // Priority 1: the inner span (what we actually want)
    var legendLabel = fieldset.querySelector('.radio__legend__label');
    if (!legendLabel) {
      // Fallback: theme rendered the legend without the span wrapper.
      // Create one and migrate the legend's inline children into it.
      var legend = fieldset.querySelector('legend.radio__legend, legend');
      if (!legend) return null;
      legendLabel = document.createElement('span');
      legendLabel.className = 'radio__legend__label label-typography mw-grace-legend-fallback';
      while (legend.firstChild) legendLabel.appendChild(legend.firstChild);
      legend.appendChild(legendLabel);
    }

    // Wrap leading "Length" text node in a stable .mw-len-text span so the
    // pill can sit next to it as a sibling flex item.
    if (!legendLabel.querySelector(':scope > .mw-len-text')) {
      for (var i = 0; i < legendLabel.childNodes.length; i++) {
        var n = legendLabel.childNodes[i];
        if (n.nodeType === 3 && n.textContent.trim()) {
          var span = document.createElement('span');
          span.className = 'mw-len-text';
          span.textContent = n.textContent.trim();
          legendLabel.replaceChild(span, n);
          break;
        }
      }
    }

    // Safety-net inline styles — guarantees flex layout even if CSS rules
    // don't match (specificity wars, late-loading stylesheets, etc.).
    legendLabel.style.display = 'flex';
    legendLabel.style.alignItems = 'center';
    legendLabel.style.gap = '8px';
    legendLabel.style.flexWrap = 'wrap';
    legendLabel.style.width = '100%';

    return legendLabel;
  }

  function updateLengthSelectedPill() {
    if (!document.body.classList.contains('milano-grace')) return;
    var lengthGroup = markGraceLengthGrid();
    var checkedInput = lengthGroup ? lengthGroup.querySelector('.radio__button input:checked') : null;
    var checked = checkedInput ? checkedInput.closest('.radio__button').querySelector('.option-title') : null;
    if (!checked) return;
    var raw = readLengthLabel(checked);
    if (!raw) return;

    var fieldset = (lengthGroup && lengthGroup.closest('fieldset')) || document.querySelector('fieldset.radio__fieldset--variant-option-image, fieldset.mw-length-fieldset');
    if (!fieldset) return;
    var legendLabel = ensureLengthLegendLayout(fieldset);
    if (!legendLabel) return;

    var pill = legendLabel.querySelector('.mw-len-selected');
    if (!pill) {
      pill = document.createElement('span');
      pill.className = 'mw-len-selected';
    }
    if (pill.getAttribute('data-mw-val') !== raw) {
      pill.textContent = '';
      var labelSpan = document.createElement('span');
      labelSpan.className = 'mw-len-selected__label';
      labelSpan.textContent = 'Selected:';
      var valSpan = document.createElement('span');
      valSpan.className = 'mw-len-selected__val';
      valSpan.textContent = raw;
      pill.appendChild(labelSpan);
      pill.appendChild(document.createTextNode(' '));
      pill.appendChild(valSpan);
      pill.setAttribute('data-mw-val', raw);
    }

    // Place pill right after .mw-len-text (the wrapped "Length" word).
    // The help button — if it exists — has CSS margin-left:auto and sits
    // on the right edge. Layout: [Length] [Pill] ........ [Compare lengths]
    var lenText = legendLabel.querySelector(':scope > .mw-len-text');
    var helpBtn = legendLabel.querySelector(':scope > .mw-lace-help, :scope > .mw-inline-help');

    if (lenText) {
      // Insert right after the wrapped Length text. Guard against Palo Alto
      // variant transitions detaching the legend between query and write.
      var desiredNext = lenText.nextSibling;
      if (pill.parentNode !== legendLabel || pill.previousSibling !== lenText) {
        try {
          legendLabel.insertBefore(pill, desiredNext && desiredNext.parentNode === legendLabel ? desiredNext : null);
        } catch (_) {
          try { legendLabel.appendChild(pill); } catch (__) {}
        }
      }
    } else if (helpBtn) {
      // Fallback: before the help button
      if (pill.parentNode !== legendLabel || pill.nextSibling !== helpBtn) {
        try {
          legendLabel.insertBefore(pill, helpBtn.parentNode === legendLabel ? helpBtn : null);
        } catch (_) {
          try { legendLabel.appendChild(pill); } catch (__) {}
        }
      }
    } else if (pill.parentNode !== legendLabel) {
      try { legendLabel.appendChild(pill); } catch (_) {}
    }

    // Cleanup stale pills from prior versions (anywhere in the fieldset
    // that's not inside our legend label)
    var stale = fieldset.querySelectorAll('.mw-len-selected');
    stale.forEach(function(el) { if (el !== pill && el.parentNode !== legendLabel) el.remove(); });
  }

  function splitGraceLengthLabels() {
    if (!document.body.classList.contains('milano-grace')) return true;
    var lengthGroup = markGraceLengthGrid();
    var titles = lengthGroup ? lengthGroup.querySelectorAll('.radio__button .option-title') : [];
    if (!titles.length) return false;
    for (var i = 0; i < titles.length; i++) {
      var el = titles[i];
      if (el.dataset.mwSplit === '1') continue;
      var raw = (el.textContent || '').trim();
      if (!raw) continue;
      var m = raw.match(/^(\d+["\u2033\u201D]?)\s+(.+)$/);
      if (!m) continue;
      el.innerHTML = '';
      var num = document.createElement('span');
      num.className = 'mw-len-num';
      num.textContent = m[1];
      var name = document.createElement('span');
      name.className = 'mw-len-name';
      name.textContent = m[2];
      el.appendChild(num);
      el.appendChild(name);
      el.dataset.mwSplit = '1';
    }
    updateLengthSelectedPill();
    return true;
  }

  // v2026-05-22c: Consolidated into MILANO_onVariantMutation pipeline.
  // Was a standalone MutationObserver (graceObserver) on variant-selects.
  // Now: registers as a SYNC subscriber with isRelevantMutation as the
  // filter. Same behavior — flicker-free synchronous split before paint
  // — but shares the single global observer instead of running its own.
  //
  // isOurOwnMutation / isRelevantMutation retained — they're now the
  // filter predicate passed to the pipeline.

  function isOurOwnMutation(target) {
    if (!target || target.nodeType !== 1) return false;
    if (!target.classList) return false;
    return target.classList.contains('mw-len-selected') ||
           target.classList.contains('mw-len-num') ||
           target.classList.contains('mw-len-name') ||
           target.classList.contains('mw-len-text');
  }

  function isRelevantMutation(m) {
    var t = m.target;
    if (isOurOwnMutation(t)) return false;
    if (t && t.nodeType === 1 && t.closest && t.closest('.radio__buttons, .radio__legend, .radio__fieldset')) {
      return true;
    }
    if (m.addedNodes) {
      for (var j = 0; j < m.addedNodes.length; j++) {
        var n = m.addedNodes[j];
        if (n.nodeType !== 1) continue;
        if (isOurOwnMutation(n)) continue;
        if (n.matches && n.matches('.radio__buttons, .radio__fieldset, fieldset')) return true;
        if (n.querySelector && n.querySelector('.radio__buttons, .option-title')) return true;
      }
    }
    return false;
  }

  var mwGraceSubscribed = false;
  var mwGraceStabilizeToken = 0;

  function scheduleGraceLabelStabilize(reason) {
    if (!document.body.classList.contains('milano-grace')) return;
    mwGraceStabilizeToken++;
    var token = mwGraceStabilizeToken;
    var run = function() {
      if (token !== mwGraceStabilizeToken) return;
      splitGraceLengthLabels();
      updateLengthSelectedPill();
    };

    // One immediate frame for responsive feel, then two delayed passes for
    // Palo Alto's viewTransition/double-buffer replacement. Newer variant
    // clicks cancel older scheduled stabilizers so rapid tapping does not
    // replay stale selected pills.
    requestAnimationFrame(run);
    setTimeout(run, 120);
    setTimeout(run, 360);
  }

  function attachGraceObserver() {
    // v2026-05-22c: subscription is idempotent — only register once per page.
    // body-class gate keeps non-Grace pages out of the sync hot path.
    if (mwGraceSubscribed) return;
    if (!document.body.classList.contains('milano-grace')) return;
    if (typeof window.MILANO_onVariantMutation !== 'function') return;
    window.MILANO_onVariantMutation(splitGraceLengthLabels, {
      sync: true,
      filter: isRelevantMutation
    });
    mwGraceSubscribed = true;
  }

  function bootstrapGraceLabelSplit() {
    // Combined task: split labels AND ensure observer is attached.
    // stopOnSuccess=false — runs all 10 attempts (no clear success signal
    // and Globo re-renders may need multiple passes).
    mwAddBootstrapTask(function() {
      splitGraceLengthLabels();
      updateLengthSelectedPill();
      attachGraceObserver();
      return false;
    }, false);

    if (!document.body.classList.contains('milano-grace')) return;
    attachGraceObserver();

    // v2026-05-19f: delegated change listener — updates the selected
    // pill the instant a radio is clicked, before MutationObserver fires.
    // v2026-05-19i: triple-attempt for resilience against Globo's
    // post-click re-render that can wipe the just-set pill.
    if (!document.body.dataset.mwLenChangeBound) {
      document.body.addEventListener('change', function(e) {
        if (e.target && e.target.matches && e.target.matches('.radio__buttons input[type="radio"]')) {
          scheduleGraceLabelStabilize('radio-change');
        }
      });
      document.body.dataset.mwLenChangeBound = '1';
    }
  }

  // v2026-05-19h: Public hook. wizard.js's watchVariantChanges calls this
  // after Globo/theme replaces the fieldset on color variant change — we
  // re-split labels, refresh the pill, and rebind the observer to the new
  // .radio__buttons element.
  // v2026-05-22c: simplified. With consolidated observer on .product__form
  // (stable parent), the observer survives Globo's variant-selects
  // replacements without rebinding. Just re-run the work that depends on
  // the freshly-rendered DOM. attachGraceObserver() remains a no-op when
  // already subscribed (mwGraceSubscribed guard).
  window.MILANO_refreshGraceLength = function() {
    if (!document.body.classList.contains('milano-grace')) return;
    splitGraceLengthLabels();
    updateLengthSelectedPill();
    scheduleGraceLabelStabilize('external-refresh');
    attachGraceObserver(); // idempotent; will register once if not yet subscribed
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrapGraceLabelSplit);
  } else {
    bootstrapGraceLabelSplit();
  }

  // ===========================================================================
  // VARIANT IMAGE PRELOAD (v2026-05-21b / wizard v1.6.35)
  //
  // Preloads higher-resolution variant images so a Length tile click triggers
  // an instant gallery swap instead of a network round-trip. Best-effort:
  // extracts URLs from existing Length tile <img> tags and upgrades to an
  // 800px-wide variant. Browser dedupes identical URLs, so this is safe to
  // call even after the user has already seen those images.
  //
  // Deferred via requestIdleCallback (with 2s timeout fallback) so it never
  // blocks initial paint or competes with above-the-fold resources.
  // Registered with the bootstrap registry to retry if Length tiles paint
  // late (Globo lazy-render).
  // ===========================================================================
  function preloadVariantImagesForGrace() {
    // v2026-05-26g: Disabled the old eager tile-image preload path.
    // The current gallery filter prefetches only on user intent, so we do
    // not enqueue every Length tile image on page load. This removes the
    // browser "preloaded but not used" warnings and keeps mobile LCP clean.
    return true;
  }

  function schedulePreload() {
    // Intentionally no-op. Kept as a compatibility marker for older comments
    // and for any external debug snippets that reference this function name.
  }

  // ===========================================================================
  // GRACE GALLERY LENGTH LABELS + CHART DETECTION (v2026-06-02)
  //
  // Replaces the older Front / Side / Back positional chips with length-aware
  // labels such as 24" Gisele. This is more useful on the Grace PDP because
  // shoppers compare the same wig across length families. The Grace Collection
  // / length chart is deliberately excluded from length labels and marked as a
  // chart so CSS can render it uncropped and the media filter can keep it out
  // of the hero position.
  //
  // Cost: one scoped DOM scan. No observer. Re-run after variant/media events
  // because Palo Alto/Flickity can replace or move gallery nodes.
  // ===========================================================================
  function normalizeGraceLabelText(value) {
    return String(value || '').toLowerCase().replace(/[“”]/g, '"').replace(/\s+/g, ' ').trim();
  }

  function isGraceChartAlt(alt) {
    var text = normalizeGraceLabelText(alt);
    if (!text) return false;
    return /grace\s+collection|length\s+(comparison|guide|chart)|comparison\s+chart|size\s+chart/.test(text);
  }

  function getGraceLengthValuesForLabels() {
    var inputs = Array.prototype.slice.call(document.querySelectorAll('input[data-single-option-selector][name="options[Length]"], input[data-single-option-selector][name*="[Length]"]'));
    var seen = Object.create(null);
    var values = [];

    inputs.forEach(function(input) {
      var value = input && input.value ? String(input.value).trim() : '';
      if (!value || seen[value]) return;
      seen[value] = true;
      values.push(value);
    });

    // Fallback for first paint if inputs are not ready yet: read visible card
    // text split by our existing Length label splitter.
    if (!values.length) {
      document.querySelectorAll('.mw-length-grid .option-title, fieldset.mw-length-fieldset .option-title').forEach(function(title) {
        var text = title.textContent ? title.textContent.replace(/\s+/g, ' ').trim() : '';
        if (text && !seen[text]) { seen[text] = true; values.push(text); }
      });
    }

    return values;
  }

  function labelForGraceMediaAlt(alt, lengthValues) {
    if (!alt || isGraceChartAlt(alt)) return '';
    var normAlt = normalizeGraceLabelText(alt);

    for (var i = 0; i < lengthValues.length; i++) {
      var value = lengthValues[i];
      if (normAlt.indexOf(normalizeGraceLabelText(value)) !== -1) return value;
    }

    return '';
  }

  function getMediaAltFromElement(el) {
    if (!el) return '';
    var img = el.matches && el.matches('img') ? el : el.querySelector && el.querySelector('img[alt], img');
    return (img && img.getAttribute('alt')) || el.getAttribute('aria-label') || el.getAttribute('title') || '';
  }

  function markGalleryMediaElement(el, lengthValues) {
    if (!el || !el.setAttribute) return false;
    var alt = getMediaAltFromElement(el);
    var isChart = isGraceChartAlt(alt);
    var label = labelForGraceMediaAlt(alt, lengthValues);

    el.toggleAttribute('data-mw-chart-media', isChart);

    if (isChart) {
      el.removeAttribute('data-mw-label');
      el.removeAttribute('data-mw-gallery-labeled');
      el.querySelectorAll('img').forEach(function(img) {
        img.setAttribute('loading', 'lazy');
        img.setAttribute('fetchpriority', 'low');
        img.setAttribute('decoding', 'async');
      });
      return true;
    }

    if (label) {
      el.setAttribute('data-mw-label', label);
      el.setAttribute('data-mw-gallery-labeled', '1');
      return true;
    }

    el.removeAttribute('data-mw-label');
    el.removeAttribute('data-mw-gallery-labeled');
    return false;
  }

  function labelGraceGallery() {
    if (!document.body.classList.contains('milano-grace')) return true;

    var scope = document.querySelector('product-info[data-product-single-media-group]') || document.querySelector('product-info');
    if (!scope) return false;

    var lengthValues = getGraceLengthValuesForLabels();
    if (!lengthValues.length) return false;

    var slides = Array.prototype.slice.call(scope.querySelectorAll('[data-product-single-media-wrapper], .product-gallery__media, .product-gallery__media-link'));
    var thumbs = Array.prototype.slice.call(scope.querySelectorAll('.product-single__thumbnail, [data-thumbnail-id]'));
    var touched = 0;

    slides.forEach(function(el) { if (markGalleryMediaElement(el, lengthValues)) touched++; });
    thumbs.forEach(function(el) { if (markGalleryMediaElement(el, lengthValues)) touched++; });

    return touched > 0;
  }

  var graceGalleryLabelRaf = 0;

  function scheduleGraceGalleryLabels() {
    if (graceGalleryLabelRaf) return;
    graceGalleryLabelRaf = requestAnimationFrame(function() {
      graceGalleryLabelRaf = 0;
      labelGraceGallery();
    });
  }

  function bootstrapGraceGalleryLabels() {
    if (!document.body.classList.contains('milano-grace')) return;

    mwAddBootstrapTask(labelGraceGallery, true);

    window.addEventListener('milano:length-media-filtered', scheduleGraceGalleryLabels);
    window.addEventListener('milano:variant-selects-replaced', scheduleGraceGalleryLabels);
    document.addEventListener('shopify:section:load', scheduleGraceGalleryLabels);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrapGraceGalleryLabels);
  } else {
    bootstrapGraceGalleryLabels();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrapTitleSplit);
  } else {
    bootstrapTitleSplit();
  }

  // =========================================================================
  // INVENTORY COPY SOFTENER (v2026-05-16)
  //
  // Replaces "Hurry! Low inventory" → "Limited availability" on the low-stock
  // state span. Walks child nodes so any leading icon/dot child element is
  // preserved untouched (only text nodes are modified). Idempotent via
  // dataset flag. Pressure-style framing erodes trust at Milano's price point;
  // calm "Limited availability" maintains scarcity signal without selling
  // urgency.
  //
  // Implemented in JS instead of editing inventory-countdown.liquid because
  // (a) the Liquid path proved fragile in prior sessions, (b) JS is reversible
  // by deleting one function, (c) text swap also covers any variants where
  // the theme renders different state markup.
  // =========================================================================

  function softenInventoryCopy() {
    if (!isPDP) return false;
    var lowSpan = document.querySelector('.variant__countdown--low');
    if (!lowSpan) return false;
    if (lowSpan.getAttribute('data-mw-softened') === '1') return true;

    // Walk text nodes only — preserve icon/dot child elements.
    var modified = false;
    var nodes = lowSpan.childNodes;
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].nodeType === 3 && nodes[i].nodeValue && nodes[i].nodeValue.trim()) {
        var newText = nodes[i].nodeValue.replace(
          /Hurry!?\s*Low inventory|Low stock|Limited stock|Only a few left/i,
          'Limited availability'
        );
        if (newText !== nodes[i].nodeValue) {
          nodes[i].nodeValue = newText;
          modified = true;
        }
      }
    }
    if (modified) lowSpan.setAttribute('data-mw-softened', '1');
    return true;
  }

  function bootstrapInventorySoften() {
    mwAddBootstrapTask(softenInventoryCopy, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrapInventorySoften);
  } else {
    bootstrapInventorySoften();
  }

  // =========================================================================
  // STATE
  // =========================================================================

  var state = {
    container: null,
    panelBuilt: false,
    building: false,
    rows: [],
    currentlyOpen: null,
    panelEl: null,
    contentEl: null,
    summaryEl: null,
    observers: []
  };

  // Module-level guard: prevents syncATCPrice() from initializing twice.
  // Must live here (not inside syncATCPrice) so repeated calls are blocked
  // across executions. Reset by shopify:section:load for theme-editor reloads.
  var _syncStarted = false;

  // =========================================================================
  // CONFIGURATION
  // =========================================================================

  var ALWAYS_VISIBLE_EXACT = [
    'adjust cap size',
    'adjust ponytail cap size',
    'adjust princess cap size',
    'topper base size',
    'fringe topper base size',
    'new cap size',
    'select cap size'
  ];

  var ALWAYS_VISIBLE_KEYWORDS = [
    'fedora', 'baseball caps', 'cap color',
    'lace front options', 'deep lace'
  ];

  // =========================================================================
  // HELP LINKS — "What's This?" popup mapping
  // Maps SC Options names → secure-popup container IDs.
  // Order matters: first match wins. Put specific patterns before general.
  // =========================================================================

  var HELP_LINKS = [
    // Secure methods — product-specific variants (check these first)
    //{ pattern: /wavy.*secure|secure.*wavy/i, popup: 'wavy-secure-method' },
    { pattern: /onyx.*secure|secure.*onyx/i, popup: 'onyx-secure-method' },
    { pattern: /diamond.*secure|secure.*diamond/i, popup: 'diamond-secure-method' },
    { pattern: /topaz.*secure|emerald.*secure|secure.*topaz|secure.*emerald/i, popup: 'topeme-secure-method' },
    { pattern: /topper.*secure|secure.*topper/i, popup: 'dtoppers-secure-method' },
    // Secure method — generic fallback (Divine, or any unmatched)
    { pattern: /secure method/i, popup: 'secure-method' },

    // Cap size / base size
    { pattern: /base size/i, popup: 'base-size-topaz-emerald' },
    { pattern: /cap size/i, popup: 'new-cap-size' },

    // Lace / front hairline
    { pattern: /lace front|deep lace/i, popup: 'lace-compare' },
    { pattern: /front hairline|blending.*hairline/i, popup: 'front-hairline' },

    // Color
    { pattern: /color service/i, popup: 'color-service' },

    // Cut, style, part
    { pattern: /personalize your cut/i, popup: 'personalize-your-cut-wigs-toppers' },
    { pattern: /signature cut/i, popup: 'milano-signature' },
    { pattern: /style my.*wavy|wavy.*style/i, popup: 'please-style-my-wig-wavy' },
    { pattern: /style my wig|please style/i, popup: 'please-style-my-wig' },
    { pattern: /location of part/i, popup: 'location-of-part' },
    { pattern: /face fram/i, popup: 'face-farming-position' },

    // Ear pieces
    { pattern: /ear piece/i, popup: 'ear-pieces' }
  ];

  function findHelpPopupId(optionName) {
    if (!optionName) return null;
    for (var i = 0; i < HELP_LINKS.length; i++) {
      if (HELP_LINKS[i].pattern.test(optionName)) {
        // Verify the popup container exists on this page
        var el = document.getElementById(HELP_LINKS[i].popup);
        if (el) return HELP_LINKS[i].popup;
      }
    }
    return null;
  }

  function createHelpLink(popupId) {
    var link = document.createElement('a');
    link.href = '#' + popupId;
    link.className = 'milano-help-link';
    link.textContent = 'What\'s This?';
    link.setAttribute('role', 'button');
    link.setAttribute('aria-label', 'Learn more');
    link.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation(); // Don't toggle accordion row
      showSecurePopup(popupId);
    });
    return link;
  }

  // =========================================================================
  // SECURE POPUP — Show / Hide
  // =========================================================================

  function showSecurePopup(id) {
    var container = document.getElementById(id);
    if (!container || !container.classList.contains('pop-up-container')) {
      // Fallback querySelector with safe escaping
      try {
        var escaped = (CSS && CSS.escape) ? CSS.escape(id) : id.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
        container = document.querySelector('.pop-up-container#' + escaped);
      } catch (e) { /* ignore */ }
    }
    if (!container) {
      if (window.MILANO_DEBUG) console.warn('[Milano] Popup not found: #' + id);
      return;
    }
    container.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    log('[Milano] Popup opened: #' + id);

    // Safety: auto-clear overflow if popup stays open >15s (mobile close can fail)
    setTimeout(function() {
      if (document.body.style.overflow === 'hidden') {
        var stillOpen = document.querySelector('.pop-up-container[style*="flex"]');
        if (stillOpen) {
          hideAllSecurePopups();
          log('[Milano] Popup overflow safety — auto-closed after 15s');
        }
      }
    }, 15000);
  }

  function hideAllSecurePopups() {
    var open = document.querySelectorAll('.pop-up-container');
    var closed = false;
    for (var i = 0; i < open.length; i++) {
      if (open[i].style.display === 'flex' || getComputedStyle(open[i]).display === 'flex') {
        open[i].style.display = 'none';
        closed = true;
      }
    }
    if (closed) {
      document.body.style.overflow = '';
      // Belt-and-suspenders: if the native size-guide drawer leaked the theme's
      // scroll lock on mobile, releasing it here is why "open Compare to unfreeze"
      // works today. Make it explicit + guarded so it never unlocks while another
      // drawer legitimately needs the lock.
      if (typeof window.MILANO_releaseScrollLock === 'function') window.MILANO_releaseScrollLock();
      log('[Milano] Popups closed');
    }
  }

  // Global close handlers (run once)
  if (!window._milanoPopupHandlers) {
    window._milanoPopupHandlers = true;

    // Close button
    document.addEventListener('click', function(e) {
      if (e.target.classList.contains('close-button-img') ||
          e.target.closest('.close-button')) {
        hideAllSecurePopups();
      }
    });

    // Backdrop click (click on .pop-up-container itself, not its children)
    document.addEventListener('click', function(e) {
      if (e.target.classList.contains('pop-up-container')) {
        hideAllSecurePopups();
      }
    });

    // ESC key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        hideAllSecurePopups();
      }
    });
  }

  // =========================================================================
  // SCROLL-LOCK SAFETY NET (v2026-06-04)
  //
  // BUG: On mobile, opening the native "Size guide" drawer then closing it left
  // the page scroll-frozen until another popup (e.g. Compare) was opened/closed.
  //
  // ROOT CAUSE: The Palo Alto theme locks scroll via the `scroll-lock` library,
  // driven by `theme:scroll:lock` / `theme:scroll:unlock` events, and marks
  // <html data-scroll-locked>. The size-guide drawer's close didn't reliably
  // fire the unlock on mobile, so `enablePageScroll()` never ran (the touchmove
  // blocker stayed installed) — only `body{overflow}` cleared, which is why
  // opening/closing a secure popup (which clears body overflow) appeared to fix
  // it. We re-dispatch the theme's OWN unlock so the library deregisters
  // correctly, but only when nothing actually needs the lock.
  // =========================================================================
  if (!window._milanoScrollLockGuard) {
    window._milanoScrollLockGuard = true;

    var somethingNeedsScrollLock = function() {
      var b = document.body;
      if (b && (b.classList.contains('js-drawer-open-cart') ||
                b.classList.contains('js-quick-view-visible'))) return true;
      // Any open drawer / native dialog.
      if (document.querySelector(
        'drawer-element[aria-hidden="false"], .drawer[aria-hidden="false"], dialog[open]'
      )) return true;
      // Any visible Milano secure popup.
      var pops = document.querySelectorAll('.pop-up-container');
      for (var i = 0; i < pops.length; i++) {
        var d = pops[i].style.display || '';
        if (d === 'flex' || d === 'block') return true;
        if (!d && getComputedStyle(pops[i]).display === 'flex') return true;
      }
      return false;
    };

    var releaseScrollLockIfLeaked = function() {
      if (!document.documentElement.hasAttribute('data-scroll-locked') &&
          document.body.style.overflow !== 'hidden') return;
      if (somethingNeedsScrollLock()) return;
      // Use the theme's own unlock so scroll-lock deregisters its touch handlers.
      try { document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true })); } catch (e) {}
      if (document.documentElement.hasAttribute('data-scroll-locked')) {
        document.documentElement.removeAttribute('data-scroll-locked');
      }
      if (document.body.style.overflow === 'hidden') document.body.style.overflow = '';
      if (window.MILANO_DEBUG) console.log('[Milano] scroll-lock leak released');
    };
    window.MILANO_releaseScrollLock = releaseScrollLockIfLeaked;

    // Watch the size-guide drawer specifically: when it closes, re-check shortly
    // after (let the theme's own close handler run first).
    var watchSizeChartDrawers = function() {
      var drawers = document.querySelectorAll('drawer-element[id^="size-chart--"]');
      for (var i = 0; i < drawers.length; i++) {
        if (drawers[i].getAttribute('data-mw-scroll-guard') === '1') continue;
        drawers[i].setAttribute('data-mw-scroll-guard', '1');
        var obs = new MutationObserver(function() {
          setTimeout(releaseScrollLockIfLeaked, 60);
          setTimeout(releaseScrollLockIfLeaked, 400);
        });
        obs.observe(drawers[i], { attributes: true, attributeFilter: ['aria-hidden', 'class'] });
      }
    };

    // Cheap user-interaction net: a tap while leaked releases on the next tick.
    document.addEventListener('touchstart', function() {
      if (document.documentElement.hasAttribute('data-scroll-locked')) {
        setTimeout(releaseScrollLockIfLeaked, 0);
      }
    }, { passive: true, capture: true });

    document.addEventListener('visibilitychange', function() {
      if (!document.hidden) setTimeout(releaseScrollLockIfLeaked, 0);
    });

    watchSizeChartDrawers();
    if (typeof window.MILANO_addBootstrap === 'function') {
      window.MILANO_addBootstrap(function() { watchSizeChartDrawers(); return false; }, false);
    } else {
      var scAttempts = 0;
      var scIv = setInterval(function() { watchSizeChartDrawers(); if (++scAttempts >= 12) clearInterval(scIv); }, 300);
    }
  }

  // =========================================================================
  // DOM: Find the correct SC Options container
  // =========================================================================

  function findOptionsContainer() {
    var loaded = document.querySelector('.bold_options.bold_options_loaded');
    if (loaded) return loaded;

    var all = document.querySelectorAll('.bold_options');
    for (var i = 0; i < all.length; i++) {
      if (all[i].querySelector('.bold_option')) return all[i];
    }
    return null;
  }

  // =========================================================================
  // DOM: Find the product <form>
  // =========================================================================

  function getProductForm() {
    return document.querySelector('form[action*="/cart/add"]')
        || document.querySelector('.product__form')
        || document.querySelector('[data-product-form]');
  }

  // =========================================================================
  // DOM: Stamp form="" attribute on all inputs inside a container
  // =========================================================================

  function stampFormAttribute(el, formId) {
    if (!el || !formId) return;
    var inputs = el.querySelectorAll('input, select, textarea');
    for (var i = 0; i < inputs.length; i++) {
      if (!inputs[i].hasAttribute('form')) {
        inputs[i].setAttribute('form', formId);
      }
    }
  }

  // =========================================================================
  // CART SUBMIT SAFEGUARD: ensure relocated SC Options stay attached
  // =========================================================================

  function ensureFormId(form) {
    if (!form || !form.setAttribute) return '';
    var formId = form.getAttribute('id');
    if (!formId) {
      formId = 'AddToCartForm-' + String(Date.now());
      form.setAttribute('id', formId);
    }
    return formId;
  }

  function stampExternalFormAttribute(el, formId, form) {
    if (!el || !formId) return;
    var inputs = el.querySelectorAll ? el.querySelectorAll('input, select, textarea') : [];
    for (var i = 0; i < inputs.length; i++) {
      // Correct stale form= ids first. A stale form attribute can override the
      // nearest ancestor form, so it must be fixed even when the input is now
      // physically back inside the active product form.
      var currentFormId = inputs[i].getAttribute('form');
      if (currentFormId && currentFormId !== formId) {
        inputs[i].setAttribute('form', formId);
        continue;
      }

      // Inputs physically inside the form do not need a form= attribute. Inputs
      // moved outside by the panel/wizard do; correct stale ids from prior
      // theme renders so line-item properties submit to the active product form.
      if (form && form.contains(inputs[i])) continue;
      if (!currentFormId) inputs[i].setAttribute('form', formId);
    }
  }

  function belongsToActiveProductScope(node, root, form) {
    if (!node || root === document) return true;

    var nodeRoot = node.closest && node.closest('product-info');
    if (nodeRoot) return nodeRoot === root;

    var nodeForm = node.closest && node.closest('form[action*="/cart/add"], .product__form, [data-product-form]');
    if (nodeForm && form && nodeForm !== form) return false;

    // Some app nodes are moved next to, not inside, product-info. Allow those
    // only when they are not clearly owned by another product form/scope.
    return true;
  }

  window.MILANO_prepareProductFormForCart = function(form) {
    form = form || getProductForm();
    if (!form || !form.matches || !form.matches('form[action*="/cart/add"], .product__form, [data-product-form]')) return false;

    var formId = ensureFormId(form);
    if (!formId) return false;

    var root = form.closest && form.closest('product-info');
    root = root || document;

    ['.bold_options', '.milano-panel', '.milano-stl-options', '.personalize-toggle'].forEach(function(selector) {
      var nodes = root.querySelectorAll ? Array.prototype.slice.call(root.querySelectorAll(selector)) : [];
      if ((!nodes || !nodes.length) && root !== document && document.querySelectorAll) {
        nodes = Array.prototype.slice.call(document.querySelectorAll(selector)).filter(function(node) {
          return belongsToActiveProductScope(node, root, form);
        });
      }
      for (var i = 0; i < nodes.length; i++) {
        stampExternalFormAttribute(nodes[i], formId, form);
      }
    });

    return true;
  };

  // =========================================================================
  // DOM: Extract option name from .bold_option element
  // =========================================================================

  function getOptionName(el) {
    // 1. data-option-name attribute
    var name = el.getAttribute('data-option-name');
    if (name && name.trim()) return name.trim();

    // 2. properties[...] from input name attributes
    var inputs = el.querySelectorAll('select, input, textarea');
    for (var i = 0; i < inputs.length; i++) {
      var n = inputs[i].getAttribute('name') || '';
      var m = n.match(/properties\[([^\]]+)\]/);
      if (m) return m[1].trim();
    }

    // 3. .bold_option_title text
    var title = el.querySelector('.bold_option_title');
    if (title) {
      var text = (title.textContent || '').trim()
        .replace(/\*$/, '').replace(/:$/, '').trim();
      if (text) return text;
    }

    // 4. First label text
    var label = el.querySelector('label');
    if (label) {
      var lt = (label.textContent || '').trim();
      if (lt && lt.length < 80) return lt;
    }

    // 5. Heading
    var heading = el.querySelector('h1,h2,h3,h4,h5,h6,strong,b,.bold_option_header');
    if (heading) return (heading.textContent || '').trim();

    return '';
  }

  // =========================================================================
  // Check if an option element has actual form content
  // =========================================================================

  function hasFormContent(el) {
    var selects = el.querySelectorAll('select');
    var inputs = el.querySelectorAll('input[type="radio"], input[type="checkbox"]');
    var textInputs = el.querySelectorAll('input[type="text"], textarea');
    var title = el.querySelector('.bold_option_title');
    return (selects.length > 0 || inputs.length > 0 || textInputs.length > 0 || (title && title.textContent.trim()));
  }

  // =========================================================================
  // CLASSIFICATION
  // =========================================================================

  function isAlwaysVisible(name) {
    var lower = name.toLowerCase();
    for (var a = 0; a < ALWAYS_VISIBLE_EXACT.length; a++) {
      if (lower === ALWAYS_VISIBLE_EXACT[a]) return true;
    }
    for (var k = 0; k < ALWAYS_VISIBLE_KEYWORDS.length; k++) {
      if (lower.indexOf(ALWAYS_VISIBLE_KEYWORDS[k]) > -1) return true;
    }
    return false;
  }

  function isPersonalizeIt(name) {
    var lower = (name || '').toLowerCase().replace(/\s+/g, ' ').trim();
    return lower === 'personalize it' ||
           lower === 'personalize this wig' ||
           lower === 'personalize this topper' ||
           /^personalize\s+this\s+/.test(lower);
  }

  // =========================================================================
  // SHOP THIS LOOK / SIMPLIFIED SC OPTION SETS
  // =========================================================================

  var _mwIsStl = false;
  var _mwStlReapplyBound = false;

  function isShopThisLookOptionName(name) {
    var lower = (name || '').toLowerCase();
    return lower.indexOf('secure method') > -1 ||
           lower.indexOf('topgrip') > -1 ||
           lower.indexOf('top grip') > -1 ||
           lower.indexOf('clips') > -1 ||
           lower.indexOf('adjust cap size') > -1 ||
           lower.indexOf('cap size') > -1 ||
           lower.indexOf('base size') > -1 ||
           lower.indexOf('ear piece') > -1 ||
           lower.indexOf('ear pieces') > -1;
  }

  function hasNativeGraceLengthOrLace() {
    if (!document.body.classList.contains('milano-grace')) return false;
    if (document.querySelector('input[data-single-option-selector][name="options[Length]"]')) return true;
    var nativeInputs = document.querySelectorAll('input[data-single-option-selector]');
    for (var n = 0; n < nativeInputs.length; n++) {
      var nm = (nativeInputs[n].getAttribute('name') || '').toLowerCase();
      if (nm.indexOf('lace') > -1) return true;
    }
    return false;
  }

  function isMinimalDirectOptionSet(alwaysVisible, personalizationOptions, personalizeIt) {
    // Option-structure based detection. This catches toppers/minimal PDPs that
    // have a personalize gate but only one or two direct SC option questions.
    // Grace/full wizard PDPs are excluded so the full step-by-step flow remains.
    var items = [];
    for (var a = 0; a < alwaysVisible.length; a++) {
      items.push({ el: alwaysVisible[a], name: getOptionName(alwaysVisible[a]) });
    }
    for (var p = 0; p < personalizationOptions.length; p++) {
      items.push(personalizationOptions[p]);
    }

    if (items.length === 0 || items.length > 4) return false;
    if (hasNativeGraceLengthOrLace() && items.length > 1) return false;

    var hasPersonalizeGate = !!(personalizeIt && personalizeIt.length > 0);
    var allMinimalNames = true;
    var hasCapOrBase = false;
    var hasSecureOrEar = false;

    for (var i = 0; i < items.length; i++) {
      var name = items[i].name || '';
      var lower = name.toLowerCase();
      if (!isShopThisLookOptionName(name)) allMinimalNames = false;
      if (lower.indexOf('cap size') > -1 || lower.indexOf('base size') > -1) hasCapOrBase = true;
      if (lower.indexOf('secure') > -1 || lower.indexOf('topgrip') > -1 || lower.indexOf('top grip') > -1 || lower.indexOf('clips') > -1 || lower.indexOf('ear piece') > -1) hasSecureOrEar = true;
    }

    // Existing Shop This Look pattern: cap/base + secure/ear, no personalize gate.
    if (!hasPersonalizeGate && allMinimalNames && hasCapOrBase && hasSecureOrEar) return true;

    // Minimal product pattern: a personalize gate exists but the actual direct
    // choices are only one/two practical option groups, e.g. topper secure method.
    if (hasPersonalizeGate && items.length <= 2) return true;

    // No gate, but all option groups are practical/minimal and not a full wizard.
    if (!hasPersonalizeGate && allMinimalNames && items.length <= 3) return true;

    return false;
  }

  function isShopThisLookSimplifiedSet(alwaysVisible, personalizationOptions, personalizeIt) {
    return isMinimalDirectOptionSet(alwaysVisible, personalizationOptions, personalizeIt);
  }

  function sortShopThisLookOptions(options) {
    options.sort(function(a, b) {
      function rank(opt) {
        var lower = (getOptionName(opt) || '').toLowerCase();
        if (lower.indexOf('cap size') > -1 || lower.indexOf('base size') > -1) return 1;
        if (lower.indexOf('secure method') > -1 || lower.indexOf('topgrip') > -1 || lower.indexOf('top grip') > -1 || lower.indexOf('clips') > -1) return 2;
        if (lower.indexOf('ear piece') > -1) return 3;
        return 10;
      }
      return rank(a) - rank(b);
    });
  }

  function enhanceShopThisLookOption(opt) {
    if (!opt) return;
    var titleEl = opt.querySelector('.bold_option_title');
    if (!titleEl) return;

    // Preserve a previously-relocated size guide before we wipe the title below.
    // SC Options / theme re-renders can rebuild this title; the native source
    // block (.product__form__size-chart) is already emptied by relocateCapSizeGuide(),
    // so without re-attaching, the size guide would vanish on rebuild.
    var preExistingGuide = titleEl.querySelector('.mw-size-guide-native');

    var name = getOptionName(opt);
    var lower = (name || '').toLowerCase();
    var cleanTitle = name.replace(/\*$/, '').trim();
    var desc = '';

    if (lower.indexOf('cap size') > -1) {
      cleanTitle = 'Adjust Cap Size';
      desc = 'Choose the best cap fit.';
    } else if (lower.indexOf('base size') > -1) {
      cleanTitle = 'Choose Base Size';
      desc = 'Choose the best base fit.';
    } else if (lower.indexOf('secure method') > -1 || lower.indexOf('topgrip') > -1 || lower.indexOf('top grip') > -1 || lower.indexOf('clips') > -1) {
      cleanTitle = 'Secure Method';
      desc = 'Choose how it stays secure.';
    } else if (lower.indexOf('ear piece') > -1) {
      cleanTitle = 'Add Ear Pieces';
      desc = 'Add side coverage near the ears.';
    }

    // Build nodes instead of writing a text string. This prevents the theme or
    // SC Options from collapsing the title + helper copy into one inline run
    // such as "Adjust Cap SizeChoose..." during app/theme re-renders.
    titleEl.textContent = '';
    var titleSpan = document.createElement('span');
    titleSpan.className = 'mw-stl-title-text';
    titleSpan.textContent = cleanTitle;
    titleEl.appendChild(titleSpan);

    if (desc) {
      var descSpan = document.createElement('span');
      descSpan.className = 'mw-stl-title-desc';
      descSpan.textContent = desc;
      titleEl.appendChild(descSpan);
    }

    // Re-attach the relocated size guide on the cap/base size card so a rebuild
    // doesn't strip it. Prefer the guide that was in this title; fall back to the
    // module-level stash set by relocateCapSizeGuide().
    if (lower.indexOf('cap size') > -1 || lower.indexOf('base size') > -1) {
      var guide = preExistingGuide || _mwCapGuideEl;
      if (guide) {
        guide.classList.add('mw-size-guide-native');
        titleEl.appendChild(guide);
        _mwCapGuideEl = guide;
      }
    }

    opt.setAttribute('data-mw-stl-enhanced', '1');
  }

  // Milano 2026-06-29: re-apply the flat STL layout after a native variant (Length)
  // switch re-renders the SC options. The wizard's doVariantWork normally re-decorates
  // on variant change, but it is skipped for STL products — without this, switching
  // Length reverts the cards to the raw SC layout. Registered once per STL product as a
  // SYNC MILANO_onVariantMutation subscriber — re-asserts in the observer microtask
  // BEFORE paint, so a strip is never visible even under stress-switching. IDEMPOTENT:
  // no-op when the layout is already intact, so it can never self-trigger a mutation loop.
  function reapplyShopThisLook() {
    if (!_mwIsStl) return;

    var toggle = document.querySelector('.personalize-toggle');
    var stlWrapper = document.querySelector('.milano-stl-options');
    var rawOpts = document.querySelectorAll('.bold_option');

    var needsFix = (!stlWrapper) || (toggle && toggle.style.display !== 'none');
    if (!needsFix) {
      for (var i = 0; i < rawOpts.length; i++) {
        var o = rawOpts[i];
        if (isPersonalizeIt(getOptionName(o))) {
          if (o.style.display !== 'none') { needsFix = true; break; }
          continue;
        }
        var te = o.querySelector('.bold_option_title');
        if (o.parentNode !== stlWrapper || (te && !te.querySelector('.mw-stl-title-text'))) {
          needsFix = true; break;
        }
      }
    }
    if (!needsFix) return;

    document.body.classList.add('milano-shop-this-look');
    document.body.classList.add('milano-minimal-options');
    if (toggle) {
      toggle.style.display = 'none';
      toggle.setAttribute('aria-hidden', 'true');
    }

    var container = findOptionsContainer();
    if (!stlWrapper && container && container.parentNode) {
      stlWrapper = document.createElement('div');
      stlWrapper.className = 'milano-stl-options';
      container.parentNode.insertBefore(stlWrapper, container);
    }
    if (!stlWrapper) return;

    var fitOpts = [];
    for (var k = 0; k < rawOpts.length; k++) {
      var opt = rawOpts[k];
      if (isPersonalizeIt(getOptionName(opt))) { opt.style.display = 'none'; continue; }
      fitOpts.push(opt);
    }
    sortShopThisLookOptions(fitOpts);
    for (var j = 0; j < fitOpts.length; j++) {
      var fo = fitOpts[j];
      fo.classList.add('milano-always-visible');
      fo.style.display = '';
      var titleEl = fo.querySelector('.bold_option_title');
      if (titleEl && !titleEl.querySelector('.mw-stl-title-text')) enhanceShopThisLookOption(fo);
      stlWrapper.appendChild(fo);
    }
  }

  function syncOptionSelectedClasses(scope) {
    scope = scope || document;
    var values = scope.querySelectorAll('.bold_option_value');
    for (var i = 0; i < values.length; i++) {
      var checked = values[i].querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
      values[i].classList.toggle('mw-selected', !!checked);
    }
  }

  // =========================================================================
  // TOGGLE STATE
  // =========================================================================

  function getPersonalizeState() {
    try {
      var checkbox = document.querySelector('[data-personalize-input][type="checkbox"]');
      if (checkbox) return checkbox.checked;
      var radio = document.querySelector('[data-personalize-input]:checked');
      if (radio) return radio.value === 'Yes';
      var sel = document.querySelector('select[name="properties[Personalize It]"]');
      if (sel) return sel.value === 'Yes';
    } catch (e) {}
    return false;
  }

  function updatePersonalizeState() {
    var isYes = getPersonalizeState();
    document.body.classList.toggle('personalize-yes', isYes);

    if (state.contentEl) {
      if (isYes) {
        state.contentEl.style.display = '';
        state.contentEl.style.maxHeight = '0px';
        void state.contentEl.offsetHeight;
        state.contentEl.style.transition = 'max-height 0.4s ease, opacity 0.3s ease';
        state.contentEl.style.opacity = '1';
        state.contentEl.style.maxHeight = state.contentEl.scrollHeight + 'px';
        setTimeout(function() {
          state.contentEl.style.maxHeight = 'none';
          state.contentEl.style.transition = '';
        }, 450);
      } else {
        collapseAllRows();
        state.contentEl.style.transition = 'max-height 0.3s ease, opacity 0.2s ease';
        state.contentEl.style.maxHeight = state.contentEl.scrollHeight + 'px';
        void state.contentEl.offsetHeight;
        state.contentEl.style.opacity = '0';
        state.contentEl.style.maxHeight = '0px';
        setTimeout(function() {
          state.contentEl.style.display = 'none';
          state.contentEl.style.transition = '';
          state.contentEl.style.opacity = '';
        }, 350);
      }
    }

    var hidden = document.querySelector('[data-personalize-hidden]');
    if (hidden) hidden.value = isYes ? 'Yes' : 'As Is';

    updateSummary();
  }

  // =========================================================================
  // SELECTION PREVIEW
  // =========================================================================

  function getSelectionPreview(boldOption) {
    var select = boldOption.querySelector('select');
    if (select && select.selectedIndex >= 0) {
      var text = select.options[select.selectedIndex].text.trim();
      if (text.indexOf('...') > -1 || text.indexOf('Select') > -1 || text === '') {
        return { text: '', hasSelection: false };
      }
      return { text: truncate(stripPrice(text), 28), hasSelection: true };
    }

    var checkedRadio = boldOption.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      var rLabel = findLabelFor(boldOption, checkedRadio);
      return { text: truncate(stripPrice(rLabel), 28), hasSelection: true };
    }

    var checked = boldOption.querySelectorAll('input[type="checkbox"]:checked');
    if (checked.length > 0) {
      if (checked.length === 1) {
        var cLabel = findLabelFor(boldOption, checked[0]);
        return { text: truncate(stripPrice(cLabel), 28), hasSelection: true };
      }
      return { text: checked.length + ' selected', hasSelection: true };
    }

    return { text: '', hasSelection: false };
  }

  function findLabelFor(container, input) {
    if (input.id) {
      var label = container.querySelector('label[for="' + input.id + '"]');
      if (label) return label.textContent.trim();
    }
    var parent = input.closest('label');
    if (parent) return parent.textContent.trim();
    var next = input.nextElementSibling;
    if (next && next.tagName === 'LABEL') return next.textContent.trim();
    return input.value || '';
  }

  function stripPrice(text) {
    return text
      .replace(/\s*\(?\+?\$[\d,]+\.?\d{0,2}\s*USD\)?/gi, '')
      .replace(/\s*\$[\d,]+\.?\d{0,2}\s*USD/gi, '')
      .trim();
  }

  function truncate(str, max) {
    if (str.length <= max) return str;
    return str.substring(0, max - 1) + '\u2026';
  }

  // =========================================================================
  // PRICE EXTRACTION
  // =========================================================================

  function extractPrice(text) {
    var match = text.match(/\+?\$?([\d,]+\.?\d{0,2})\s*USD/i);
    if (match) return parseFloat(match[1].replace(/,/g, ''));
    match = text.match(/\$(\d+(?:\.\d{2})?)/);
    if (match) return parseFloat(match[1]);
    return 0;
  }

  function getOptionPrice(boldOption) {
    var total = 0;

    var select = boldOption.querySelector('select');
    if (select && select.selectedIndex >= 0) {
      total += extractPrice(select.options[select.selectedIndex].text);
      return total;
    }

    var checkedRadio = boldOption.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      var rLabel = findLabelFor(boldOption, checkedRadio);
      total += extractPrice(rLabel);
      return total;
    }

    var checked = boldOption.querySelectorAll('input[type="checkbox"]:checked');
    checked.forEach(function(cb) {
      var wrapper = cb.closest('.bold_option_value');
      var priceEl = wrapper ? wrapper.querySelector('.bold_option_value_price') : null;
      if (priceEl) {
        total += extractPrice(priceEl.textContent);
      } else {
        var cLabel = findLabelFor(boldOption, cb);
        total += extractPrice(cLabel);
      }
    });

    return total;
  }

  // =========================================================================
  // OPTION ROW
  // =========================================================================

  function createOptionRow(boldOption, name) {
    var row = document.createElement('div');
    row.className = 'milano-option-row';
    row.setAttribute('data-option-name', name);

    // Header
    var header = document.createElement('button');
    header.type = 'button';
    header.className = 'milano-option-row__header';
    header.setAttribute('aria-expanded', 'false');

    var nameSpan = document.createElement('span');
    nameSpan.className = 'milano-option-row__name';
    nameSpan.textContent = name;

    var previewSpan = document.createElement('span');
    previewSpan.className = 'milano-option-row__preview';

    var chevron = document.createElement('span');
    chevron.className = 'milano-option-row__chevron';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 14 14" fill="none">' +
        '<path d="M3.5 5.25L7 8.75L10.5 5.25" stroke="currentColor" stroke-width="1.5" ' +
        'stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';

    header.appendChild(nameSpan);

    // Help link — "What's This?"
    var helpPopupId = findHelpPopupId(name);
    if (helpPopupId) {
      header.appendChild(createHelpLink(helpPopupId));
    }

    header.appendChild(previewSpan);
    header.appendChild(chevron);

    // Body
    var body = document.createElement('div');
    body.className = 'milano-option-row__body';

    boldOption.parentNode.insertBefore(row, boldOption);
    body.appendChild(boldOption);
    row.appendChild(header);
    row.appendChild(body);

    var rowData = {
      row: row,
      header: header,
      body: body,
      nameSpan: nameSpan,
      previewSpan: previewSpan,
      boldOption: boldOption,
      name: name,
      isOpen: false,
      isVisible: true
    };

    // Initial visibility check
    var computedDisplay = window.getComputedStyle(boldOption).display;
    var inlineDisplay = boldOption.style.display;
    var isHidden = inlineDisplay === 'none' || computedDisplay === 'none';
    var isEmpty = !hasFormContent(boldOption);

    if (isHidden || isEmpty) {
      row.classList.add('milano-option-row--hidden');
      rowData.isVisible = false;
    }

    updateRowPreview(rowData);

    // Click handler
    header.addEventListener('click', function(e) {
      e.preventDefault();
      if (rowData.isOpen) {
        collapseRow(rowData);
      } else {
        if (state.currentlyOpen && state.currentlyOpen !== rowData) {
          collapseRow(state.currentlyOpen);
        }
        expandRow(rowData);
      }
    });

    // Watch for input changes
    body.addEventListener('change', function() {
      setTimeout(function() {
        updateRowPreview(rowData);
        updateSummary();
        if (rowData.isOpen) {
          body.style.maxHeight = 'none';
        }
      }, 50);
    });

    return rowData;
  }

  function updateRowPreview(rowData) {
    var preview = getSelectionPreview(rowData.boldOption);
    rowData.previewSpan.textContent = preview.text;
    rowData.row.classList.toggle('milano-option-row--selected', preview.hasSelection);
  }

  function expandRow(rowData) {
    rowData.isOpen = true;
    state.currentlyOpen = rowData;
    rowData.header.setAttribute('aria-expanded', 'true');
    rowData.row.classList.add('milano-option-row--open');

    var body = rowData.body;
    body.style.display = '';
    body.style.maxHeight = '0px';
    void body.offsetHeight;
    body.style.transition = 'max-height 0.35s ease';
    body.style.maxHeight = body.scrollHeight + 'px';
    setTimeout(function() {
      body.style.maxHeight = 'none';
      body.style.overflow = 'visible';
      body.style.transition = '';
    }, 380);
  }

  function collapseRow(rowData) {
    rowData.isOpen = false;
    if (state.currentlyOpen === rowData) state.currentlyOpen = null;
    rowData.header.setAttribute('aria-expanded', 'false');
    rowData.row.classList.remove('milano-option-row--open');

    var body = rowData.body;
    body.style.overflow = 'hidden';
    body.style.transition = '';
    body.style.maxHeight = body.scrollHeight + 'px';
    void body.offsetHeight;
    body.style.transition = 'max-height 0.3s ease';
    body.style.maxHeight = '0px';
    setTimeout(function() {
      body.style.transition = '';
    }, 350);
  }

  function collapseAllRows() {
    state.rows.forEach(function(rowData) {
      if (rowData.isOpen) collapseRow(rowData);
    });
  }

  // =========================================================================
  // SC OPTIONS CONDITION WATCHER
  // =========================================================================

  function watchOptionVisibility(rowData) {
    var boldOption = rowData.boldOption;
    var debounceTimer = null;

    var observer = new MutationObserver(function() {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function() {
        try {
          if (!rowData.row || !rowData.boldOption) return;

          var nowHidden = boldOption.style.display === 'none';
          var nowEmpty = !hasFormContent(boldOption);
          var wasVisible = rowData.isVisible;

          if ((nowHidden || nowEmpty) && wasVisible) {
            rowData.isVisible = false;
            if (rowData.row) rowData.row.classList.add('milano-option-row--hidden');
            if (rowData.isOpen) collapseRow(rowData);
            updateSummary();
          } else if (!nowHidden && !nowEmpty && !wasVisible) {
            if (rowData.name.indexOf('Option ') === 0) {
              var newName = getOptionName(boldOption);
              if (newName && newName.indexOf('Option ') !== 0) {
                rowData.name = newName;
                if (rowData.nameSpan) rowData.nameSpan.textContent = newName;
                if (rowData.row) rowData.row.setAttribute('data-option-name', newName);
              }
            }
            rowData.isVisible = true;
            if (rowData.row) {
              rowData.row.classList.remove('milano-option-row--hidden');
              rowData.row.classList.add('milano-option-row--entering');
              setTimeout(function() {
                if (rowData.row) rowData.row.classList.remove('milano-option-row--entering');
              }, 400);
            }
            updateSummary();
          }
        } catch (e) {
          log('[Milano] Observer error:', e.message);
        }
      }, 80);
    });

    observer.observe(boldOption, {
      attributes: true,
      attributeFilter: ['style'],
      childList: true,
      subtree: false
    });

    state.observers.push(observer);
  }

  // =========================================================================
  // SUMMARY
  // =========================================================================

  function updateSummary() {
    if (!state.summaryEl) return;

    var items = [];
    var total = 0;

    state.rows.forEach(function(rowData) {
      if (!rowData.isVisible) return;

      var preview = getSelectionPreview(rowData.boldOption);
      if (!preview.hasSelection) return;

      var price = getOptionPrice(rowData.boldOption);
      total += price;

      var checked = rowData.boldOption.querySelectorAll('input[type="checkbox"]:checked');
      if (checked.length > 1) {
        checked.forEach(function(cb) {
          var label = stripPrice(findLabelFor(rowData.boldOption, cb));
          var wrapper = cb.closest('.bold_option_value');
          var priceEl = wrapper ? wrapper.querySelector('.bold_option_value_price') : null;
          var p = priceEl ? extractPrice(priceEl.textContent) : extractPrice(findLabelFor(rowData.boldOption, cb));
          if (label && label.toLowerCase() !== 'no color service') {
            items.push({ name: label, price: p });
          }
        });
      } else {
        items.push({ name: rowData.name, value: preview.text, price: price });
      }
    });

    if (items.length === 0) {
      // v1.6.41: also clear innerHTML, not just hide. Prior code left stale
      // .milano-summary__item elements in DOM (just hidden via CSS class).
      // updateReviewCard queries document for .milano-summary__item and
      // re-displayed cleared selections from the stale DOM (e.g. WiGrip +
      // $15 showing after clear, despite total being correct and cart
      // payload being clean). Empty innerHTML is the source-of-truth fix.
      state.summaryEl.innerHTML = '';
      state.summaryEl.classList.remove('milano-summary--visible');
      return;
    }

    state.summaryEl.classList.add('milano-summary--visible');

    var html = '<div class="milano-summary__label">Your Selections</div>';
    html += '<div class="milano-summary__items">';

    items.forEach(function(item) {
      html += '<div class="milano-summary__item">';
      html += '<span class="milano-summary__item-name">';
      if (item.value) {
        html += escapeHtml(item.name) + ': <strong>' + escapeHtml(item.value) + '</strong>';
      } else {
        html += escapeHtml(item.name);
      }
      html += '</span>';
      if (item.price > 0) {
        html += '<span class="milano-summary__item-price">+$' + item.price.toFixed(2) + '</span>';
      }
      html += '</div>';
    });

    html += '</div>';

    if (total > 0) {
      html += '<div class="milano-summary__total">';
      html += '<span>Added services</span>';
      html += '<span>+$' + total.toFixed(2) + '</span>';
      html += '</div>';
    }

    state.summaryEl.innerHTML = html;
  }

  // v1.6.40: expose updateSummary so wizard.js can force a refresh of the
  // .milano-summary__item DOM before reading it. Fixes a stale-display bug
  // where the review card showed selections (e.g. WiGrip + $15) that had
  // been cleared from cart payload + total computation but never had a
  // 'change' event we observed. Calling this synchronously before reading
  // the summary guarantees the DOM reflects current input state.
  window.MILANO_refreshSummary = function() {
    try { updateSummary(); } catch (e) { /* ignore */ }
  };

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // =========================================================================
  // PANEL BUILDER
  // =========================================================================

  function buildPanel() {
    if (state.panelBuilt || state.building) return;

    var container = state.container || findOptionsContainer();
    if (!container) {
      log('[Milano] No options container found');
      return;
    }

    var allOptions = container.querySelectorAll('.bold_option');
    if (allOptions.length === 0) {
      log('[Milano] No .bold_option elements found');
      return;
    }

    state.building = true;
    log('[Milano] Building panel with ' + allOptions.length + ' options...');

    // Classify
    var alwaysVisible = [];
    var personalizeIt = [];
    var personalizationOptions = [];

    allOptions.forEach(function(opt) {
      var name = getOptionName(opt);
      if (isPersonalizeIt(name)) {
        personalizeIt.push(opt);
      } else if (isAlwaysVisible(name)) {
        alwaysVisible.push(opt);
      } else {
        personalizationOptions.push({ el: opt, name: name });
      }
    });

    log('[Milano] Always visible: ' + alwaysVisible.length +
      ', Personalize It: ' + personalizeIt.length +
      ', Personalization: ' + personalizationOptions.length);

    // -----------------------------------------------------------------------
    // DEFENSIVE FALLBACK: "Shop This Look" and simplified option sets
    // -----------------------------------------------------------------------
    var hasToggle = !!document.querySelector('.personalize-toggle');
    var isSimplifiedShopThisLook = isShopThisLookSimplifiedSet(alwaysVisible, personalizationOptions, personalizeIt);
    var isMinimalDirect = isMinimalDirectOptionSet(alwaysVisible, personalizationOptions, personalizeIt);
    // Milano 2026-06-29: explicit STL force. Products carrying the milano.shop_this_look
    // metafield / shop-this-look·shop_look tag (Liquid emits <div data-mw-stl-product>)
    // render the flat STL layout even when structural detection would exclude them (e.g.
    // Grace length+lace products with >1 fit group). personalize-wizard.js skips the
    // step-by-step wizard for the same marker so the two presentations never collide.
    // GUARD: honor the marker only for genuinely fit-only sets. If a promoted option is
    // clearly a Color/Cut/Style group, ignore the marker and fall through to the normal
    // wizard — so a mis-tagged full personalize product is never flattened.
    var forcedStl = !!document.querySelector('[data-mw-stl-product]');
    if (forcedStl) {
      var MW_NON_FIT = /color\s*service|signature\s*cut|personalize\s*your\s*cut|style\s*my\s*wig|rooting|\btoning\b|balayage|lowlight|highlight/i;
      for (var fp = 0; fp < personalizationOptions.length; fp++) {
        if (MW_NON_FIT.test(personalizationOptions[fp].name || '')) { forcedStl = false; break; }
      }
    }

    if (forcedStl || isSimplifiedShopThisLook || isMinimalDirect || (personalizeIt.length === 0 && !hasToggle && personalizationOptions.length > 0)) {
      log('[Milano] Simplified/minimal option set detected — promoting ' +
        personalizationOptions.length + ' option(s) to always-visible');
      document.body.classList.add('milano-shop-this-look');
      document.body.classList.add('milano-minimal-options');

      // Milano 2026-06-29: mark STL + register the variant-change re-apply so a Length
      // switch (which re-renders the SC options) re-asserts this flat layout instead of
      // reverting to the raw SC cards. The wizard's own re-apply is skipped for STL.
      _mwIsStl = true;
      if (!_mwStlReapplyBound && typeof window.MILANO_onVariantMutation === 'function') {
        _mwStlReapplyBound = true;
        // SYNC (not debounced) + no filter: re-assert the STL layout in the SAME
        // observer microtask as the strip, BEFORE the browser paints — so even rapid
        // stress-switching never shows a revert. Runs once per observer callback and the
        // cheap needsFix early-return makes the hot path read-only; the idempotent
        // no-op-when-intact guard is what keeps it loop-safe (sync subs intentionally
        // bypass the write-lock, so idempotency — not the lock — is the protection).
        window.MILANO_onVariantMutation(reapplyShopThisLook, { sync: true });
      }

      personalizationOptions.forEach(function(item) {
        alwaysVisible.push(item.el);
      });
      personalizationOptions = [];
      sortShopThisLookOptions(alwaysVisible);

      // Hide the separate personalize entry control for products where the
      // available SC Options are already direct, clear questions. Keeping it
      // would create an unnecessary extra decision before Add to Cart.
      var simplifiedToggle = document.querySelector('.personalize-toggle');
      if (simplifiedToggle) {
        simplifiedToggle.style.display = 'none';
        simplifiedToggle.setAttribute('aria-hidden', 'true');
      }
    }

    // Hide "Personalize It"
    personalizeIt.forEach(function(opt) {
      opt.style.display = 'none';
    });

    // Always-visible: ensure shown + add styling class + move above toggle
    var toggle = document.querySelector('.personalize-toggle');
    var productForm = getProductForm();
    // NOTE: Shopify product forms contain <input name="id"> for the variant
    // selector. HTML's named-element lookup makes `productForm.id` return
    // that input element, not the form's id attribute. Use getAttribute('id')
    // to get the string id reliably. See v2026-05-11 changelog.
    var formId = productForm ? productForm.getAttribute('id') : null;

    // -----------------------------------------------------------------------
    // FORM CONTAINMENT FIX
    // -----------------------------------------------------------------------
    if (toggle && productForm && !productForm.contains(toggle)) {
      container.parentNode.insertBefore(toggle, container);
    }

    // For Shop This Look products (no toggle), wrap all always-visible options
    var stlWrapper = null;
    if (document.body.classList.contains('milano-shop-this-look') && alwaysVisible.length > 0) {
      stlWrapper = document.createElement('div');
      stlWrapper.className = 'milano-stl-options';
      container.parentNode.insertBefore(stlWrapper, container);
    }

    alwaysVisible.forEach(function(opt) {
      opt.style.display = '';
      opt.classList.add('milano-always-visible');

      // Add "What's This?" help link to the title
      var titleEl = opt.querySelector('.bold_option_title');
      if (titleEl && !titleEl.querySelector('.milano-help-link') && !titleEl.querySelector('.bold-help-link')) {
        var optName = getOptionName(opt);
        var helpId = findHelpPopupId(optName);
        if (helpId) {
          titleEl.appendChild(createHelpLink(helpId));
        }
      }

      if (stlWrapper) {
        enhanceShopThisLookOption(opt);
        stlWrapper.appendChild(opt);
      } else if (toggle && toggle.parentNode) {
        toggle.parentNode.insertBefore(opt, toggle);
      } else {
        container.parentNode.insertBefore(opt, container);
      }
    });

    if (stlWrapper) syncOptionSelectedClasses(stlWrapper);

    // Put the size guide on the "Adjust Cap Size" line for the layouts the
    // wizard doesn't relocate (shop-this-look + wizard-no-lace). Idempotent;
    // the bootstrap registration retries if the native block paints later.
    relocateCapSizeGuide();

    if (personalizationOptions.length === 0) {
      if (formId) {
        alwaysVisible.forEach(function(opt) {
          stampFormAttribute(opt, formId);
        });
      }
      state.building = false;
      state.panelBuilt = true;
      log('[Milano] No personalization options — all options always-visible');
      return;
    }

    // Build panel
    var panel = document.createElement('div');
    panel.className = 'milano-panel';

    var content = document.createElement('div');
    content.className = 'milano-panel__content';
    content.style.display = 'none';
    content.style.overflow = 'hidden';

    personalizationOptions.forEach(function(item, idx) {
      var displayName = item.name || 'Option ' + (idx + 1);
      var rowData = createOptionRow(item.el, displayName);
      state.rows.push(rowData);
      content.appendChild(rowData.row);
      watchOptionVisibility(rowData);
    });

    // Summary
    var summary = document.createElement('div');
    summary.className = 'milano-summary';
    state.summaryEl = summary;
    content.appendChild(summary);

    panel.appendChild(content);
    state.panelEl = panel;
    state.contentEl = content;

    if (toggle && toggle.parentNode) {
      toggle.parentNode.insertBefore(panel, toggle.nextSibling);
    } else if (container.nextSibling) {
      container.parentNode.insertBefore(panel, container.nextSibling);
    } else {
      container.parentNode.appendChild(panel);
    }

    if (formId) {
      stampFormAttribute(panel, formId);
      alwaysVisible.forEach(function(opt) {
        stampFormAttribute(opt, formId);
      });
    }

    if (toggle) {
      toggle.classList.add('personalize-toggle--ready');
    }

    var qtyWrapper = document.querySelector('.selector-wrapper--qty');
    var submitHolder = document.querySelector('.product__submit__holder');
    if (qtyWrapper && submitHolder) {
      submitHolder.parentNode.insertBefore(qtyWrapper, submitHolder);
    }

    state.panelBuilt = true;
    state.building = false;
    log('[Milano] Panel built: ' + state.rows.length + ' rows');

    if (getPersonalizeState()) {
      state.contentEl.style.display = '';
      state.contentEl.style.maxHeight = 'none';
      state.contentEl.style.opacity = '1';
      document.body.classList.add('personalize-yes');
    }

    updateSummary();

    addVariantHelpLinks();

    // v2026-05-22c: Consolidated into MILANO_onVariantMutation pipeline.
    // Was: standalone helpLinkObserver (MutationObserver on .product__selectors
    // or .product__form .product__block) + 500ms setInterval URL polling.
    // Both fired addVariantHelpLinks() on variant/URL changes.
    //
    // Now: single pipeline subscription. No filter — addVariantHelpLinks is
    // idempotent and cheap (skips elements that already have help links).
    // The URL polling is redundant because Globo/Shopify variant changes
    // trigger DOM mutations in the form, which the pipeline observer catches.
    if (typeof window.MILANO_onVariantMutation === 'function') {
      window.MILANO_onVariantMutation(addVariantHelpLinks);
    } else {
      // Defensive fallback if pipeline somehow isn't loaded.
      var variantArea = document.querySelector('.product__selectors, .product__form .product__block');
      if (variantArea) {
        var helpLinkObserver = new MutationObserver(function() {
          addVariantHelpLinks();
        });
        helpLinkObserver.observe(variantArea, { childList: true, subtree: true });
        state.observers.push(helpLinkObserver);
      }
    }

    if (window.location.search.indexOf('customize=true') !== -1) {
      var checkbox = document.querySelector('[data-personalize-input][type="checkbox"]');
      if (checkbox && !checkbox.checked) {
        checkbox.checked = true;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        log('[Milano] Auto-opened customization via URL param');
      }
    }
  }

  // =========================================================================
  // VARIANT HELP LINKS
  // =========================================================================

  function addVariantHelpLinks() {
    var labels = document.querySelectorAll(
      '.radio__legend__label.label-typography, .select__label.label-typography'
    );
    for (var i = 0; i < labels.length; i++) {
      var text = labels[i].textContent.trim();
      var helpId = findHelpPopupId(text);
      if (helpId && !labels[i].querySelector('.milano-help-link')) {
        labels[i].appendChild(createHelpLink(helpId));
        log('[Milano] Help link added to variant: "' + text + '" → #' + helpId);
      }
    }
  }

  // =========================================================================
  // INITIALIZATION
  // =========================================================================

  function waitForBoldOptions(callback) {
    var attempts = 0;
    var maxAttempts = 40;

    function check() {
      attempts++;
      var container = findOptionsContainer();
      if (container) {
        var count = container.querySelectorAll('.bold_option').length;
        log('[Milano] Found container (' + count + ' options, attempt ' + attempts + ')');
        state.container = container;
        callback();
        return;
      }
      if (attempts < maxAttempts) {
        setTimeout(check, 500);
      } else {
        log('[Milano] Gave up after ' + attempts + ' attempts');
        callback();
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        setTimeout(check, 300);
      });
    } else {
      setTimeout(check, 300);
    }
  }

  // =========================================================================
  // EVENT HANDLERS
  // =========================================================================

  document.addEventListener('change', function(e) {
    var target = e.target;
    if (!target) return;
    try {
      if (target.closest && target.closest('.bold_option')) {
        syncOptionSelectedClasses(target.closest('.milano-stl-options') || target.closest('.bold_option'));
      }
      if (target.matches('[data-personalize-input]') ||
          target.matches('select[name="properties[Personalize It]"]') ||
          target.matches('input[name="properties[Personalize It]"]')) {
        updatePersonalizeState();
      }
    } catch (ex) {}
  });

  document.addEventListener('shopify:section:load', function() {
    state.observers.forEach(function(o) { o.disconnect(); });
    state.observers = [];
    if (state._stopPriceSync) state._stopPriceSync();
    if (state._stopVariantUrlCheck) state._stopVariantUrlCheck();
    _syncStarted = false;
    state.panelBuilt = false;
    state.building = false;
    state.container = null;
    state.rows = [];
    state.currentlyOpen = null;
    // Section re-render produces a fresh native size-chart block; drop the stale
    // relocated-trigger reference so relocateCapSizeGuide() re-grabs the new one.
    _mwCapGuideEl = null;

    var existing = document.querySelector('.milano-panel');
    if (existing) existing.remove();

    setTimeout(function() {
      state.container = findOptionsContainer();
      buildPanel();
    }, 500);
  });

  // =========================================================================
  // ATC BUTTON + CART BAR PRICE SYNC
  // =========================================================================

  // =========================================================================
  // DN DISCOUNT DETECTION — DOM parsing with DN-specific selectors
  // =========================================================================

  function syncATCPrice() {

    // ===================================================================
    // DN DISCOUNT — DOM parsing with DN-specific selectors (READ-ONLY)
    //
    // getDNDiscountCents() reads DN's rendered elements. Never writes to DN.
    // v2026-05-07a: Removed all DN-write functions (updateDNDisplay,
    // updateDNDataAttrs, getDNOriginalPriceCents, getDNDiscountRatio,
    // resetDNVariantBase, findDNStrikeTextEl, weAreWritingDN) to fix
    // cart drawer price corruption caused by PDP write-war loop.
    // ===================================================================

    var _dnCache = { info: null, ts: 0, suppressed: null };
    var _lastBaseTrusted = false;       // set by getVariantBaseCents(): true only when base came from a trusted source
    var _milanoPriceReadyFired = false; // milano:price-ready dispatched once

    function readMilanoSaleConfig() {
      // Retired: the previous theme-side sale configuration calculated a price
      // from a percentage. Promotion Display v2 accepts only DN-confirmed values.
      return null;
    }

    // Sale activation is intentionally strict:
    //   1) a Milano campaign config is explicitly enabled, or
    //   2) Discount Ninja's live PDP sale DOM is currently detected.
    // We do NOT activate sale pricing from sessionStorage. A stored ratio can
    // outlive a promotion in the same browser session and make PDP prices look
    // discounted after DN has stopped rendering the promo.

    function readDNRenderedSaleInfo() {
      var promotionConfig = window.MILANO_PROMOTION_DISPLAY_V2;
      if (promotionConfig && promotionConfig.enabled) {
        var confirmed = window.MILANO_PROMOTION_DISPLAY_PDP_RESULT;
        var productRoot = document.querySelector('[data-promotion-display-surface="pdp"][data-promotion-eligible="true"]');
        var currentVariantId = productRoot && productRoot.getAttribute('data-promotion-variant-id');
        if (!confirmed || (confirmed.variantId && currentVariantId && String(confirmed.variantId) !== String(currentVariantId))) return null;

        return {
          originalCents: confirmed.originalCents,
          saleCents: confirmed.discountedCents,
          discountCents: confirmed.discountCents,
          ratio: confirmed.ratio,
          percentOff: confirmed.percentage,
          label: confirmed.label,
          source: confirmed.source || 'promotion-display-confirmed'
        };
      }

      var strikeEl = document.querySelector('.limoniapps-discountninja-strikethrough');
      var saleEl = document.querySelector('.limoniapps-discountninja-shortcode-discounted_price');
      if (!strikeEl || !saleEl) return null;

      var originalCents = 0;
      var saleCents = 0;
      var origAttr = strikeEl.querySelector('[data-limoniapps-discountninja-price-cents-basecurrency]');
      var saleAttr = saleEl.querySelector('[data-limoniapps-discountninja-price-cents-basecurrency]');

      if (origAttr && saleAttr) {
        originalCents = parseInt(origAttr.getAttribute('data-limoniapps-discountninja-price-cents-basecurrency'), 10) || 0;
        saleCents = parseInt(saleAttr.getAttribute('data-limoniapps-discountninja-price-cents-basecurrency'), 10) || 0;
      }

      if (!(originalCents > saleCents && saleCents > 0)) {
        originalCents = parseCents(strikeEl.textContent);
        saleCents = parseCents(saleEl.textContent);
      }

      if (!(originalCents > saleCents && saleCents > 0)) return null;

      var ratio = saleCents / originalCents;
      if (!(ratio > 0 && ratio < 1)) return null;

      var info = {
        originalCents: originalCents,
        saleCents: saleCents,
        discountCents: originalCents - saleCents,
        ratio: ratio,
        percentOff: Math.round((1 - ratio) * 100),
        label: Math.round((1 - ratio) * 100) + '% off applied',
        source: 'discount-ninja-dom'
      };
      return info;
    }

    // An active Promotion Display campaign follows its Liquid collection gate,
    // which can intentionally include Accessories. Outside that campaign, keep
    // the legacy generic DN-DOM fallback off accessories and native markdowns so
    // an unrelated sale cannot revive the historical ratio leak.
    function saleSuppressedForVariant() {
      var promotionConfig = window.MILANO_PROMOTION_DISPLAY_V2;
      if (promotionConfig && promotionConfig.enabled) {
        var promotionRoot = document.querySelector('[data-promotion-display-surface="pdp"]');
        return !promotionRoot || promotionRoot.getAttribute('data-promotion-eligible') !== 'true';
      }

      var pinfo = document.querySelector('product-info[data-mw-accessory], [data-mw-accessory]');
      if (pinfo && pinfo.getAttribute('data-mw-accessory') === 'true') return true;
      if ((document.body.className || '').toLowerCase().indexOf('accessor') > -1) return true;
      var fw = document.querySelector('product-form[data-form-wrapper]');
      if (fw && parseInt(fw.getAttribute('data-variant-compare-price') || '0', 10) > 0) return true;
      var cmp = document.querySelector('[data-price-wrapper]:not([data-atc-button]) [data-compare-price]');
      if (cmp && cmp.offsetParent !== null && parseCents(cmp.textContent) > 0) return true;
      return false;
    }

    function getDNSaleInfo() {
      var now = Date.now();
      // Evaluate suppression EVERY call and fold it into the cache key, so a
      // variant flip that changes eligibility (accessory<->wig, compare-at<->none)
      // is reflected immediately — even if no variant event fired to invalidate
      // the cache. The 800ms TTL still spares the expensive DN-DOM read.
      var suppressed = saleSuppressedForVariant();
      if (_dnCache.info && now - _dnCache.ts < 800 && _dnCache.suppressed === suppressed) {
        return _dnCache.info;
      }

      var none = {
        originalCents: 0,
        saleCents: 0,
        discountCents: 0,
        ratio: 1,
        percentOff: 0,
        label: '',
        source: 'none'
      };
      var info = suppressed
        ? none
        : (readMilanoSaleConfig() || readDNRenderedSaleInfo() || none);

      _dnCache = { info: info, ts: now, suppressed: suppressed };
      return info;
    }

    function getDNDiscountCents() {
      var info = getDNSaleInfo();
      return info && info.discountCents ? info.discountCents : 0;
    }

    function invalidateDNCache() {
      _dnCache.info = null;
      _dnCache.ts = 0;
    }


    // ===================================================================
    // PRICE ELEMENT FINDERS
    //
    // v2026-05-07a: Removed findDNStrikeTextEl() — was only used by
    // updateDNDisplay() which has been removed.
    // ===================================================================

    // SC Options managed price — variant + add-ons (may be hidden by DN)
    function findSCOptionsEl() {
      return document.querySelector('[data-product-price].bold_option_price_display, .bold_option_price_display [data-product-price]');
    }

    // DN's visible sale price text — uses DN-specific classes only (safe, no false positives)
    function findDNSaleTextEl() {
      return document.querySelector(
        '.limoniapps-discountninja-shortcode-discounted_price .limoniapps-discountninja-money > .money,' +
        '.limoniapps-discountninja-shortcode-discounted_price .limoniapps-discountninja-money'
      );
    }

    // ATC price elements removed — ATC price hidden via CSS (v2026-05-07a).
    // DN and Palo Alto can write to ATC freely; it's hidden, no conflict.

    // Cart bar price
    function findCartBarPriceEl() {
      return document.querySelector('.cart-bar [data-product-price]');
    }

    // ===================================================================
    // PRICE PARSING & FORMATTING
    // ===================================================================

    function parseCents(text) {
      if (!text) return 0;
      var cleaned = (text + '').replace(/[^\d.]/g, '');
      return Math.round(parseFloat(cleaned) * 100) || 0;
    }

    function formatUSD(cents) {
      var promotionConfig = window.MILANO_PROMOTION_DISPLAY_V2;
      if (window.theme && typeof window.theme.formatMoney === 'function' && promotionConfig && promotionConfig.moneyFormat) {
        return window.theme.formatMoney(cents, promotionConfig.moneyFormat);
      }

      var d = (cents / 100).toFixed(2);
      var parts = d.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return '$' + parts[0] + '.' + parts[1];
    }


    function getVariantBaseCents() {
      var fw = document.querySelector('product-form[data-form-wrapper]');
      if (fw) {
        var cents = parseInt(fw.getAttribute('data-variant-price') || '0', 10);
        if (cents > 0) { _lastBaseTrusted = true; return cents; }
      }

      var productInfo = document.querySelector('product-info');
      if (productInfo) {
        var selectedJSON = productInfo.querySelector('variant-selects [data-selected-variant]');
        if (selectedJSON && selectedJSON.textContent) {
          try {
            var variant = JSON.parse(selectedJSON.textContent);
            if (variant && variant.price) { _lastBaseTrusted = true; return parseInt(variant.price, 10) || 0; }
          } catch (_) { /* ignore malformed theme JSON */ }
        }
      }

      // Last-resort fallback: the visible price text can transiently include
      // add-ons during a native re-render, so mark it UNTRUSTED — computeFinalPrice
      // will not apply a base-only ratio to it (avoids over-discounting).
      _lastBaseTrusted = false;
      var regEl = document.querySelector('[data-price-wrapper]:not([data-atc-button]) [data-product-price]');
      return regEl ? parseCents(regEl.textContent) : 0;
    }

    function isPersonalizeEnabled() {
      var checkbox = document.querySelector('[data-personalize-input][type="checkbox"]');
      return !checkbox || checkbox.checked;
    }

    function optionPriceCentsFromValue(valueEl) {
      if (!valueEl) return 0;
      var priceEl = valueEl.querySelector('.bold_option_value_price');
      if (!priceEl) return 0;
      return parseCents(priceEl.textContent || '');
    }

    // v2026-06-04: hardened against the Cap Size "+$200" double-count.
    // Cap Size is a set of radios inside a .bold_option.milano-always-visible row.
    // When Palo Alto re-renders on a Length/Lace switch, SC Options can briefly
    // leave a STALE widget mounted (e.g. Large still :checked) alongside the LIVE
    // widget (e.g. Small the shopper just clicked). The old de-dupe keyed on
    // name|value, so two DIFFERENT values of the SAME option both counted → $200.
    // Fix: collapse every single-select group (radios, selects) to exactly ONE
    // price, preferring the control that is actually laid out (the live widget).
    // Genuine multi-select checkbox groups (e.g. multiple colour add-ons) still
    // sum, de-duping cloned controls by value.
    function elementIsLaidOut(el) {
      return !!(el && el.getClientRects && el.getClientRects().length > 0);
    }

    // The value that actually submits is the one inside the live add-to-cart
    // form, so prefer that when a stale SC Options clone duplicates a group.
    function inLiveCartForm(el) {
      return !!(el && el.closest && el.closest('form[action*="/cart/add"]'));
    }

    // Higher rank wins a single-select group; ties resolve to later DOM order
    // (the freshly-rendered widget). Prefer the submitting-form control first,
    // then a laid-out one — so the DISPLAYED price matches what's CHARGED, even
    // when an off-screen stale clone is briefly :checked with a different value.
    function selectionRank(el) {
      return (inLiveCartForm(el) ? 2 : 0) + (elementIsLaidOut(el) ? 1 : 0);
    }

    function groupKeyForRow(row, input, index) {
      var byAttr = (row.getAttribute('data-option-name') || '').trim().toLowerCase();
      if (byAttr) return byAttr;
      // Fall back to the visible option title (e.g. "Cap Size") so cloned widgets
      // that differ in input `name` still collapse into one single-select group.
      var titleEl = row.querySelector('.bold_option_title');
      var byTitle = titleEl ? (titleEl.textContent || '').trim().toLowerCase() : '';
      if (byTitle) return byTitle;
      return (input && input.name ? input.name : '') || ('__row-' + index);
    }

    // Keep one entry per single-select group, preferring the highest-ranked
    // control (submitting-form > laid-out), tie-breaking to later DOM order.
    function recordSingleSelect(map, order, key, cents, rank) {
      var prev = map[key];
      if (!prev) {
        map[key] = { cents: cents, rank: rank };
        order.push(key);
      } else if (rank >= prev.rank) {
        map[key] = { cents: cents, rank: rank };
      }
    }

    function getSelectedServiceCents() {
      var personalizeEnabled = isPersonalizeEnabled();
      var checkboxCents = 0;
      var seen = {};                 // checkbox de-dupe across clones: groupKey|value
      var singleByGroup = {};        // radios + selects: groupKey -> { cents, rank }
      var singleOrder = [];

      var checked = document.querySelectorAll('.bold_option input:checked');
      for (var i = 0; i < checked.length; i++) {
        var input = checked[i];
        if (input.disabled) continue;
        var row = input.closest('.bold_option');
        if (!row) continue;

        // Cap Size and other always-visible SC Options are real line-item
        // property add-ons even when the personalization wizard is closed.
        // Wizard-only service rows are counted only when Personalize It is on.
        if (!personalizeEnabled && !row.classList.contains('milano-always-visible')) continue;

        var groupKey = groupKeyForRow(row, input, i);
        var valueEl = input.closest('.bold_option_value') || input.closest('label') || row;
        var cents = optionPriceCentsFromValue(valueEl);

        if ((input.type || '').toLowerCase() === 'radio') {
          // SINGLE-SELECT: one price per group, regardless of stale clones.
          recordSingleSelect(singleByGroup, singleOrder, groupKey, cents, selectionRank(input));
        } else {
          // MULTI-SELECT (checkboxes): sum distinct values, de-duping clones.
          var key = groupKey + '|' + (input.value || '');
          if (seen[key]) continue;
          seen[key] = true;
          checkboxCents += cents;
        }
      }

      var selects = document.querySelectorAll('.bold_option select');
      for (var s = 0; s < selects.length; s++) {
        var select = selects[s];
        var selectRow = select.closest('.bold_option');
        if (!selectRow) continue;
        if (!personalizeEnabled && !selectRow.classList.contains('milano-always-visible')) continue;
        if (select.disabled || select.selectedIndex < 0) continue;
        var selected = select.options[select.selectedIndex];
        if (!selected || !selected.value) continue;
        var selectedText = selected.textContent || '';
        if (selectedText.indexOf('$') === -1) continue;
        // Selects are inherently single-select; collapse clones to one per group.
        recordSingleSelect(singleByGroup, singleOrder, groupKeyForRow(selectRow, select, 1000 + s), parseCents(selectedText), selectionRank(select));
      }

      var total = checkboxCents;
      for (var g = 0; g < singleOrder.length; g++) total += singleByGroup[singleOrder[g]].cents;
      return total;
    }

    window.MILANO_getSelectedServiceCents = getSelectedServiceCents;

    // ===================================================================
    // COMPUTE FINAL PRICE
    //
    // Single source of truth for the final price shown in cart bar, review
    // card, and BNPL. Combines SC Options total (raw, undiscounted) with
    // any flat discount reported by DN's DOM (when DN is active on PDP).
    //
    // v2026-05-12: Post-Mother's-Day teardown — ratio-based discount path
    // removed along with storeDNRatio(). The dnDiscount flat fallback
    // remains (defensive — handles a future direct-discount sale where DN
    // renders on PDP). For percentage-of-total sales, restore the ratio
    // logic in lockstep with storeDNRatio().
    //
    // Priority:
    //  1. SC Options active + DN flat discount → SC_total − flat_discount
    //  2. SC Options active, no DN            → SC_total (unchanged)
    //  3. No SC Options, DN active            → DN sale price (already correct)
    //  4. Neither                              → Regular Palo Alto price
    //
    // Returns { finalCents, displayText, scTotalCents, dnDiscountCents }
    // or null if no price source found.
    // ===================================================================

    function applySaleToRegularTotal(regularCents, saleInfo) {
      var finalCents = regularCents;
      var source = 'none';
      var label = '';
      var ratio = saleInfo && saleInfo.ratio;

      if (ratio > 0 && ratio < 0.999) {
        finalCents = Math.max(0, Math.round(regularCents * ratio));
        source = saleInfo.source || 'discount-ratio';
        label = saleInfo.label || (Math.round((1 - ratio) * 100) + '% off applied');
      } else if (saleInfo && saleInfo.discountCents > 0) {
        finalCents = Math.max(0, regularCents - saleInfo.discountCents);
        source = saleInfo.source || 'discount-flat';
        label = saleInfo.label || 'Sale applied';
      }

      return {
        finalCents: finalCents,
        discountCents: Math.max(0, regularCents - finalCents),
        isDiscounted: finalCents < regularCents,
        discountSource: source,
        discountLabel: label,
        discountRatio: ratio || 1
      };
    }

    function computeFinalPrice() {
      var saleInfo = getDNSaleInfo();
      var baseCents = getVariantBaseCents();
      var selectedServiceCents = getSelectedServiceCents();
      var scEl = findSCOptionsEl();
      var scCents = 0;

      if (scEl) {
        scCents = parseCents(scEl.textContent);
      }

      // SC Options is the source of truth for add-on prices, but Palo Alto can
      // reset the visible price to the bare variant during native Length/Lace
      // renders. getSelectedServiceCents() is now authoritative — it returns ONE
      // price per single-select group — so we must NOT Math.max() it against the
      // SC-rendered total. That max latched a transient double-count (a stale Cap
      // Size widget briefly co-existing with the live one) and stuck the price at
      // +$200. Use the SC total ONLY as a fallback when our DOM read found nothing
      // (i.e. mid-render when no add-on inputs are readable yet).
      // Promotion Display v2 never re-calculates Discount Ninja's discount.
      // It uses DN's confirmed base-product cents and adds selected services at
      // full price, which is the requested base-only promotion behavior.
      var hasConfirmedPromotion = saleInfo &&
        saleInfo.source === 'promotion-display-confirmed' &&
        saleInfo.originalCents > saleInfo.saleCents &&
        saleInfo.saleCents > 0;

      var serviceCents = selectedServiceCents;
      if (serviceCents === 0 && baseCents > 0 && scCents > baseCents + 50 && (!hasConfirmedPromotion || _lastBaseTrusted)) {
        serviceCents = scCents - baseCents;
      }

      if (hasConfirmedPromotion) {
        var confirmedFinalWithOptions = saleInfo.saleCents + serviceCents;
        var confirmedRegularWithOptions = saleInfo.originalCents + serviceCents;
        return {
          finalCents: confirmedFinalWithOptions,
          displayText: formatUSD(confirmedFinalWithOptions),
          regularCents: confirmedRegularWithOptions,
          scTotalCents: confirmedRegularWithOptions,
          serviceCents: serviceCents,
          dnDiscountCents: saleInfo.discountCents,
          isDiscounted: true,
          discountSource: saleInfo.source,
          discountLabel: saleInfo.label,
          discountRatio: saleInfo.ratio || 1
        };
      }

      if (baseCents > 0) {
        var regularWithOptions = baseCents + serviceCents;
        var hasRatioSale = saleInfo && saleInfo.ratio > 0 && saleInfo.ratio < 0.999;

        // Services are NOT discounted: a percentage (ratio) sale applies to the
        // base wig ONLY; add-ons are added at full price. Only take this path when
        // the base came from a TRUSTED source (variant-price attr / variant JSON),
        // never the textContent fallback (which can include add-ons mid-render).
        if (hasRatioSale && _lastBaseTrusted) {
          var saleOnBase = applySaleToRegularTotal(baseCents, saleInfo);
          var finalWithOptions = saleOnBase.finalCents + serviceCents;
          return {
            finalCents: finalWithOptions,
            displayText: formatUSD(finalWithOptions),
            regularCents: regularWithOptions,
            scTotalCents: regularWithOptions,
            serviceCents: serviceCents,
            dnDiscountCents: saleOnBase.discountCents,
            isDiscounted: saleOnBase.isDiscounted,
            discountSource: saleOnBase.discountSource,
            discountLabel: saleOnBase.discountLabel,
            discountRatio: saleOnBase.discountRatio
          };
        }

        // Ratio sale but base is UNTRUSTED this tick: do not ratio (avoid
        // over-discounting a base+add-ons value); show the full regular total now.
        // A later trusted tick re-applies the discount. Stale-safe.
        if (hasRatioSale) {
          return {
            finalCents: regularWithOptions,
            displayText: formatUSD(regularWithOptions),
            regularCents: regularWithOptions,
            scTotalCents: regularWithOptions,
            serviceCents: serviceCents,
            dnDiscountCents: 0,
            isDiscounted: false,
            discountSource: 'none',
            discountLabel: '',
            discountRatio: 1
          };
        }

        // No ratio sale: a flat DN-DOM discount (a fixed base-discount amount) or
        // none. Subtracting a flat base discount from the total already yields
        // base-only behaviour (services stay full), so this path is unchanged.
        var sale = applySaleToRegularTotal(regularWithOptions, saleInfo);
        return {
          finalCents: sale.finalCents,
          displayText: formatUSD(sale.finalCents),
          regularCents: regularWithOptions,
          scTotalCents: regularWithOptions,
          serviceCents: serviceCents,
          dnDiscountCents: sale.discountCents,
          isDiscounted: sale.isDiscounted,
          discountSource: sale.discountSource,
          discountLabel: sale.discountLabel,
          discountRatio: sale.discountRatio
        };
      }

      if (scCents > 0) {
        // Fail closed when the base variant price is temporarily unavailable.
        // SC's visible total may already include paid options, so applying a DN
        // ratio here would discount the whole line. Show the undiscounted SC
        // total until a trusted base price or a confirmed DN base result arrives.
        return {
          finalCents: scCents,
          displayText: formatUSD(scCents),
          regularCents: scCents,
          scTotalCents: scCents,
          serviceCents: 0,
          dnDiscountCents: 0,
          isDiscounted: false,
          discountSource: 'none',
          discountLabel: '',
          discountRatio: 1
        };
      }

      // No SC Options — check DN sale price (already correct, just read it)
      if (saleInfo && saleInfo.discountCents > 0) {
        var dnSaleEl = findDNSaleTextEl();
        if (dnSaleEl) {
          var dnCents = parseCents(dnSaleEl.textContent);
          if (dnCents > 0) {
            return {
              finalCents: dnCents,
              displayText: formatUSD(dnCents),
              regularCents: saleInfo.originalCents || 0,
              scTotalCents: 0,
              serviceCents: 0,
              dnDiscountCents: saleInfo.discountCents,
              isDiscounted: true,
              discountSource: saleInfo.source || 'discount-ninja-dom',
              discountLabel: saleInfo.label || 'Sale applied',
              discountRatio: saleInfo.ratio || 1
            };
          }
        }
      }

      // Fallback: regular Palo Alto price
      var regEl = document.querySelector(
        '[data-price-wrapper]:not([data-atc-button]) [data-product-price]'
      );
      if (regEl) {
        var regCents = parseCents(regEl.textContent);
        if (regCents > 0) {
          return {
            finalCents: regCents,
            displayText: formatUSD(regCents),
            regularCents: regCents,
            scTotalCents: 0,
            serviceCents: 0,
            dnDiscountCents: 0,
            isDiscounted: false,
            discountSource: 'none',
            discountLabel: '',
            discountRatio: 1
          };
        }
      }

      return null;
    }

    // ===================================================================
    // SYNC FUNCTIONS
    //
    // v2026-05-07a: Removed updateDNDisplay() call from doSyncFast().
    // We no longer write to DN's DOM — DN owns its own price display.
    // We only write to ATC button and cart bar (plain text, always correct).
    // ===================================================================

    var syncRunning = true;
    var lastSyncedBar = '';
    var lastFallbackSplitit = '';
    var lastKlarnaSourcePrice = '';
    var dnCooldownUntil = 0; // pause sync while DN re-renders

    function isLiveElement(el) {
      return !!(el && el.nodeType === 1 && el.isConnected);
    }

    function isHiddenByTransition(el) {
      var node = el;
      while (node && node.nodeType === 1 && node !== document.body) {
        if (node.style && node.style.display === 'none') return true;
        if (node.hasAttribute && node.hasAttribute('hidden')) return true;
        node = node.parentElement;
      }
      return false;
    }

    function findLivePDPPriceEl() {
      var els = document.querySelectorAll('[data-price-wrapper]:not([data-atc-button]) [data-product-price]');
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        if (isLiveElement(el) && !isHiddenByTransition(el)) return el;
      }
      return null;
    }

    function findLivePriceWrapper() {
      var priceEl = findLivePDPPriceEl();
      if (priceEl) {
        return priceEl.closest('[data-price-wrapper]') || priceEl.closest('.product__price-and-badge');
      }
      var wrappers = document.querySelectorAll('[data-price-wrapper]:not([data-atc-button]), .product__price-and-badge');
      for (var i = 0; i < wrappers.length; i++) {
        if (isLiveElement(wrappers[i]) && !isHiddenByTransition(wrappers[i])) return wrappers[i];
      }
      return null;
    }

    function safePlaceAfter(anchor, el, fallbackParent) {
      if (!el) return false;
      if (anchor && isLiveElement(anchor) && anchor.parentNode) {
        try {
          anchor.insertAdjacentElement('afterend', el);
          return true;
        } catch (_) { /* parent changed during Palo Alto transition */ }
      }
      if (fallbackParent && isLiveElement(fallbackParent)) {
        try {
          fallbackParent.appendChild(el);
          return true;
        } catch (_) { /* parent changed during Palo Alto transition */ }
      }
      return false;
    }

    function removeDuplicateNodes(selector, keepEl) {
      if (!selector || !keepEl) return;
      var nodes = document.querySelectorAll(selector);
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i] !== keepEl && nodes[i].parentNode) {
          try { nodes[i].parentNode.removeChild(nodes[i]); } catch (_) {}
        }
      }
    }

    function ensurePriceContext() {
      var priceWrapper = findLivePriceWrapper();
      if (!priceWrapper) return null;

      var contextEl = priceWrapper.querySelector('.mw-price-context') || document.querySelector('.mw-price-context');
      if (!contextEl) {
        contextEl = document.createElement('span');
        contextEl.className = 'mw-price-context';
        contextEl.style.display = 'none';
      }

      if (contextEl.parentNode !== priceWrapper) {
        var priceEl = priceWrapper.querySelector('[data-product-price]');
        safePlaceAfter(priceEl, contextEl, priceWrapper);
      }

      if (contextEl.isConnected) removeDuplicateNodes('.mw-price-context', contextEl);
      return contextEl.isConnected ? contextEl : null;
    }

    function ensureSaleComparePrice(priceEl) {
      // Palo Alto's HTMLUpdateUtility temporarily double-buffers and removes
      // product-info nodes during native variant changes. This function must be
      // safe if any node is detached between query and write.
      if (!isLiveElement(priceEl) || isHiddenByTransition(priceEl)) return null;

      var row = priceEl.closest('.product__price') || priceEl.parentElement;
      if (!isLiveElement(row) || !row.contains(priceEl)) return null;

      var compareEl = row.querySelector('.mw-pdp-compare-price');
      if (!compareEl) {
        compareEl = document.createElement('span');
        compareEl.className = 'mw-pdp-compare-price';
        compareEl.setAttribute('aria-hidden', 'true');
      }

      if (compareEl.parentNode !== row) {
        try {
          // insertAdjacentElement reads the live parent at write time and is less
          // brittle than parent.insertBefore(staleBeforeNode) during transitions.
          priceEl.insertAdjacentElement('beforebegin', compareEl);
        } catch (_) {
          try { row.appendChild(compareEl); } catch (__) { return null; }
        }
      }

      return isLiveElement(compareEl) ? compareEl : null;
    }

    function ensureSaleContext() {
      var priceWrapper = findLivePriceWrapper();
      if (!priceWrapper) return null;

      var contextEl = priceWrapper.querySelector('.mw-sale-context') || document.querySelector('.mw-sale-context');
      if (!contextEl) {
        contextEl = document.createElement('span');
        contextEl.className = 'mw-sale-context';
        contextEl.style.display = 'none';
      }

      if (contextEl.parentNode !== priceWrapper) {
        var priceContext = priceWrapper.querySelector('.mw-price-context');
        var priceEl = priceWrapper.querySelector('[data-product-price]');
        safePlaceAfter(priceContext || priceEl, contextEl, priceWrapper);
      }

      if (contextEl.isConnected) removeDuplicateNodes('.mw-sale-context', contextEl);
      return contextEl.isConnected ? contextEl : null;
    }

    function syncKlaviyoReadablePrice(p, priceEl) {
      if (!p || !priceEl) return;
      var dollars = (p.finalCents / 100).toFixed(2);
      priceEl.setAttribute('data-mw-final-price-cents', String(p.finalCents));
      priceEl.setAttribute('data-mw-final-price', dollars);
      priceEl.setAttribute('data-mw-regular-price-cents', String(p.regularCents || p.finalCents));
      priceEl.setAttribute('data-mw-discount-source', p.discountSource || 'none');
      priceEl.setAttribute('data-mw-discounted', p.isDiscounted ? 'true' : 'false');
      priceEl.setAttribute('data-mw-percent-off', String(Math.round((1 - (p.discountRatio || 1)) * 100)));

      window.MILANO_PDP_PRICE = {
        price: parseFloat(dollars),
        priceCents: p.finalCents,
        regularPriceCents: p.regularCents || p.finalCents,
        selectedOptionsCents: p.serviceCents || 0,
        discountCents: p.dnDiscountCents || 0,
        discountSource: p.discountSource || 'none',
        isDiscounted: !!p.isDiscounted
      };

      // Fire once, and ONLY after a discount is actually reflected, so a Klaviyo
      // (or analytics) pixel gating on this never captures the pre-discount MSRP.
      // Non-sale products don't need it — their MSRP is already the real price.
      if (!_milanoPriceReadyFired && p.isDiscounted) {
        _milanoPriceReadyFired = true;
        try { document.dispatchEvent(new CustomEvent('milano:price-ready', { detail: window.MILANO_PDP_PRICE })); } catch (_) {}
      }
    }

    function syncPDPPriceAndContext(p) {
      if (!p || !p.displayText) return;

      var serviceCents = parseInt(p.serviceCents || 0, 10);
      var isDiscounted = !!p.isDiscounted;
      // When the sale is rendered by Discount Ninja on the PDP, DN owns the price
      // line (discounted base + strikethrough). The theme must NOT overwrite it
      // (that caused the variant-swap fight); the selected-options explanation
      // stays in the separate context line below.
      var dnOwnsPrice = (p.discountSource === 'discount-ninja-dom' || p.discountSource === 'promotion-display-confirmed');
      var activePromotionConfig = window.MILANO_PROMOTION_DISPLAY_V2;
      var activePromotionRoot = document.querySelector('[data-promotion-display-surface="pdp"]');
      var waitingForDiscountNinja = !!(
        activePromotionConfig && activePromotionConfig.enabled && activePromotionRoot &&
        activePromotionRoot.getAttribute('data-promotion-eligible') === 'true'
      );
      var priceEl = findLivePDPPriceEl();
      if (priceEl) {
        var ownedBefore = priceEl.getAttribute('data-mw-price-owned') === 'services' ||
                          priceEl.getAttribute('data-mw-price-owned') === 'sale';
        if (dnOwnsPrice) {
          // Leave DN's rendered discounted base price in place. Release any prior
          // theme ownership / sale class so we never overwrite or fight DN's number.
          if (ownedBefore) {
            priceEl.classList.remove('mw-pdp-sale-price');
            priceEl.removeAttribute('data-mw-price-owned');
          }
        } else if (serviceCents > 0 || isDiscounted || ownedBefore) {
          if ((priceEl.textContent || '').trim() !== p.displayText) {
            priceEl.textContent = p.displayText;
          }
          priceEl.classList.toggle('mw-pdp-sale-price', isDiscounted);
          priceEl.setAttribute('data-mw-price-owned', isDiscounted ? 'sale' : (serviceCents > 0 ? 'services' : ''));
          if (!isDiscounted && serviceCents === 0) priceEl.removeAttribute('data-mw-price-owned');
        }

        syncKlaviyoReadablePrice(p, priceEl);

        // Lift the native-variant settling mask the instant the resolved
        // discounted price is in the DOM — so the masked window ends exactly when
        // the correct price shows, not on a fixed 240ms timer. This kills the
        // variant-swap (Lace Front etc.) price flicker. Scoped away from Grace,
        // whose settling class also gates its duplicate-fieldset masking.
        if ((!waitingForDiscountNinja || dnOwnsPrice || isDiscounted) && document.body && !document.body.classList.contains('milano-grace')) {
          document.body.classList.remove('mw-native-variant-settling');
        }

        if (!waitingForDiscountNinja || dnOwnsPrice || isDiscounted) {
          releaseInitialPDPPriceGuard();
        }

        try {
          // DN owns the strikethrough whenever it is actually rendering its own
          // (.limoniapps-discountninja-strikethrough) — which is exactly the element
          // readDNRenderedSaleInfo reads. Key the suppression on that DOM presence,
          // NOT just on discountSource: getDNSaleInfo resolves
          // readMilanoSaleConfig() || readDNRenderedSaleInfo(), so if a Milano
          // campaign config wins that precedence race while DN is ALSO live on the
          // PDP, discountSource is 'milano-config' yet DN's strike is still in the
          // DOM — the theme must still not add a second one. Hide ALL theme compare
          // nodes (also clears a stale node left in a double-buffered/transitioning
          // subtree). When the theme genuinely owns the price (DN not rendering),
          // the else branch restores .mw-pdp-compare-price as before.
          var priceRow = priceEl.closest('.product__price') || priceEl.parentElement;
          var dnStrikeLive = !!(priceRow && priceRow.querySelector('.limoniapps-discountninja-strikethrough'));
          if (dnOwnsPrice || dnStrikeLive) {
            // Hide ALL theme compare nodes (a stale one can linger in a
            // double-buffered/transitioning subtree outside the live row).
            var staleCompare = document.querySelectorAll('.mw-pdp-compare-price');
            for (var ci = 0; ci < staleCompare.length; ci++) staleCompare[ci].style.display = 'none';
          } else {
            var compareEl = ensureSaleComparePrice(priceEl);
            if (compareEl) {
              if (isDiscounted && p.regularCents && p.regularCents > p.finalCents) {
                compareEl.textContent = formatUSD(p.regularCents);
                compareEl.style.display = '';
              } else {
                compareEl.style.display = 'none';
              }
            }
          }
        } catch (_) {
          // Price decoration is non-critical. Never let it block Splitit,
          // selected-options context, cart bar, or Klaviyo-readable pricing.
        }
      }

      var contextEl = serviceCents > 0 ? ensurePriceContext() : document.querySelector('.mw-price-context');
      var saleContextEl = isDiscounted ? ensureSaleContext() : document.querySelector('.mw-sale-context');
      var priceWrap = document.querySelector('.product__price-and-badge');
      if (priceWrap) {
        priceWrap.classList.toggle('mw-has-services', serviceCents > 0);
        priceWrap.classList.toggle('mw-has-discount', isDiscounted);
      }
      if (contextEl) {
        // DN owns the discounted base price. When paid options are selected,
        // explain the add-on amount in a separate context line without writing
        // into DN's price DOM. Add-on cents remain outside the sale calculation.
        if (serviceCents > 0) {
          contextEl.classList.remove('mw-price-context--total');
          contextEl.textContent = 'Includes ' + formatUSD(serviceCents) + ' in selected options';
          contextEl.style.display = '';
        } else {
          contextEl.classList.remove('mw-price-context--total');
          contextEl.style.display = 'none';
        }
      }
      if (saleContextEl) {
        if (isDiscounted) {
          saleContextEl.textContent = p.discountLabel || 'Sale price applied';
          saleContextEl.style.display = '';
        } else {
          saleContextEl.style.display = 'none';
        }
      }
    }

    // --- Fast sync: cart bar only ---
    // v2026-05-07a: ATC button price hidden via CSS — no ATC writes needed.
    // DN owns the product price display area (strikethrough formatting).
    // We own the cart bar (clean number, correct final price with add-ons).
    function doSyncFast() {
      try {
        var p = computeFinalPrice();
        if (!p) { if (window.MILANO_DEBUG) console.warn('[Milano] Splitit skipped: computeFinalPrice()=null'); return null; }

        syncPDPPriceAndContext(p);
        if (p.finalCents && p.finalCents > 0) {
          var __splititOk = ensureSplititFallback(p.finalCents / 100, p.displayText);
          if (window.MILANO_DEBUG && !__splititOk) console.warn('[Milano] Splitit fallback returned false (no live price wrapper?) cents=' + p.finalCents);
        } else if (window.MILANO_DEBUG) {
          console.warn('[Milano] Splitit skipped: finalCents=' + (p && p.finalCents));
        }

        var bar = findCartBarPriceEl();
        var promotionConfig = window.MILANO_PROMOTION_DISPLAY_V2;
        var cartBarEnabled = !promotionConfig || !promotionConfig.enabled || !promotionConfig.surfaces || promotionConfig.surfaces.cartBar !== false;
        if (cartBarEnabled && bar && bar.textContent !== p.displayText) {
          bar.textContent = p.displayText;
          if (p.displayText !== lastSyncedBar) {
            log('[Milano] Cart bar synced → ' + p.displayText);
            lastSyncedBar = p.displayText;
          }
        }
        return p;
      } catch (e) { return null; /* elements detached — next tick retries */ }
    }




    // --- Splitit fallback: stable owned row under PDP price ---
    function getDirectChild(parent, selector) {
      if (!parent) return null;
      var children = parent.children || [];
      for (var i = 0; i < children.length; i++) {
        if (children[i].matches && children[i].matches(selector)) return children[i];
      }
      return null;
    }

    function findPDPPriceOuterWrapper() {
      var priceEl = findLivePDPPriceEl()
                 || document.querySelector('[data-product-price].bold_option_price_display')
                 || document.querySelector('[data-product-price]');
      var dataWrapper = priceEl ? priceEl.closest('[data-price-wrapper]') : null;
      var wrapper = (dataWrapper && dataWrapper.closest('.product__price-wrapper'))
                 || document.querySelector('.product__price-wrapper');
      return isLiveElement(wrapper) && !isHiddenByTransition(wrapper) ? wrapper : null;
    }

    function ensureSplititFallback(amount, displayKey) {
      // Prefer the live, non-transition-hidden wrapper; fall back to any connected
      // .product__price-wrapper so a transient display:none ancestor (wizard /
      // Palo Alto re-render window) does not permanently drop the Splitit row.
      var priceWrapper = findPDPPriceOuterWrapper()
        || (function () { var w = document.querySelector('.product__price-wrapper'); return (w && w.isConnected) ? w : null; })();
      if (!priceWrapper || !amount || amount <= 0) return false;

      var fallback = getDirectChild(priceWrapper, '.milano-splitit-fallback')
                  || document.querySelector('.milano-splitit-fallback');

      if (!fallback) {
        fallback = document.createElement('div');
        fallback.className = 'milano-splitit-fallback';
        fallback.setAttribute('role', 'note');

        var priceRow = getDirectChild(priceWrapper, '.product__price');
        if (priceRow && priceRow.nextSibling) {
          try { priceWrapper.insertBefore(fallback, priceRow.nextSibling); } catch (_) { priceWrapper.appendChild(fallback); }
        } else {
          priceWrapper.appendChild(fallback);
        }
      } else if (fallback.parentNode !== priceWrapper) {
        priceWrapper.appendChild(fallback);
      }

      var nativeWrapper = getDirectChild(priceWrapper, '.splitit-wrapper') || priceWrapper.querySelector('.splitit-wrapper');
      var perMo = Math.ceil(amount / 3).toLocaleString('en-US', {
        maximumFractionDigits: 0
      });
      var key = displayKey + '|splitit|' + perMo;

      if (fallback.getAttribute('data-mw-splitit-key') !== key) {
        fallback.innerHTML = 'From <span class="mw-splitit-price">$' + perMo + '</span>/month with ' +
          '<span class="mw-splitit-logo" aria-label="Splitit">Splitit</span>' +
          ' <a href="https://www.splitit.com/" target="_blank" rel="noopener">Learn more</a>';
        fallback.setAttribute('data-mw-splitit-key', key);
      }

      fallback.style.display = 'flex';

      // Keep the native app container available for its SDK/data attributes,
      // but prevent duplicate customer-facing rows. The Milano fallback is the
      // stable visible row; native Splitit can be torn down by Palo Alto renders.
      if (nativeWrapper && nativeWrapper.querySelector('#splitit-container')) {
        nativeWrapper.style.display = 'none';
        nativeWrapper.setAttribute('aria-hidden', 'true');
      }

      removeDuplicateNodes('.milano-splitit-fallback', fallback);

      if (displayKey !== lastFallbackSplitit) {
        log('[Milano] Splitit fallback stable: $' + amount.toFixed(2) + ' ($' + perMo + '/mo)');
        lastFallbackSplitit = displayKey;
      }

      return true;
    }

    // --- Slow sync: BNPL (Splitit + Klarna) — only on price change ---
    function doSyncBNPL(p) {
      try {
        p = p || computeFinalPrice();
        if (!p || p.finalCents <= 0) return;

        syncPDPPriceAndContext(p);

        var amount = p.finalCents / 100; // dollars

        // The Milano fallback is the customer-facing source of truth. Keep any
        // native Splitit container hidden but updated so the SDK still has its
        // data attributes without controlling layout or visibility.
        ensureSplititFallback(amount, p.displayText);

        var splititContainer = document.getElementById('splitit-container');
        if (splititContainer) {
          var oldAmount = parseFloat(splititContainer.getAttribute('data-splitit-amount') || '0');
          if (Math.abs(amount - oldAmount) > 0.01) {
            splititContainer.setAttribute('data-splitit-amount', amount.toFixed(2));
            try {
              if (window.splitit && window.splitit.ui && typeof window.splitit.ui.refresh === 'function') {
                window.splitit.ui.refresh();
              }
            } catch (e2) { /* ignore third-party refresh errors */ }
          }
        }

        // ----- Klarna -----
        if (p.displayText !== lastKlarnaSourcePrice) {
          lastKlarnaSourcePrice = p.displayText;
          var amountCents = Math.round(amount * 100);

          var setKlarnaAmount = function() {
            var el = document.querySelector('klarna-placement[data-purchase-amount]');
            if (el) el.setAttribute('data-purchase-amount', amountCents.toString());
          };

          setKlarnaAmount();
          setTimeout(setKlarnaAmount, 500);
          setTimeout(setKlarnaAmount, 1200);

          log('[Milano] Klarna synced to ' + amountCents + ' cents ($' + amount.toFixed(2) + ')');
        }
      } catch (e) { /* elements detached — next scheduled sync retries */ }
    }


    // ===================================================================
    // PRICE SYNC SCHEDULER — event-driven with a light health check
    // ===================================================================

    var healthInterval = null;
    var burstTimer = null;
    var burstUntil = 0;
    var lastObservedDisplay = '';
    var scheduledPriceSyncTimer = null;
    var scheduledPriceSyncToken = 0;
    var scheduledVerifyTimers = [];

    function clearScheduledVerifications() {
      for (var i = 0; i < scheduledVerifyTimers.length; i++) {
        clearTimeout(scheduledVerifyTimers[i]);
      }
      scheduledVerifyTimers = [];
    }

    function safeFullPriceSync(reason) {
      if (!syncRunning) return;
      try {
        log('[Milano] PDP price sync → ' + (reason || 'scheduled'));
        runPriceSync();
      } catch (e) {
        log('[Milano] PDP price sync skipped; DOM still transitioning', e);
      }
    }

    function schedulePDPPriceSync(reason, opts) {
      if (!syncRunning) return;
      opts = opts || {};
      var token = ++scheduledPriceSyncToken;
      var delay = typeof opts.delay === 'number' ? opts.delay : 70;

      if (scheduledPriceSyncTimer) clearTimeout(scheduledPriceSyncTimer);
      clearScheduledVerifications();

      scheduledPriceSyncTimer = setTimeout(function() {
        scheduledPriceSyncTimer = null;
        requestAnimationFrame(function() {
          if (token !== scheduledPriceSyncToken) return;
          safeFullPriceSync(reason);
        });
      }, delay);

      if (opts.verify !== false) {
        [260, 760].forEach(function(ms) {
          scheduledVerifyTimers.push(setTimeout(function() {
            if (token !== scheduledPriceSyncToken) return;
            safeFullPriceSync(reason + ':verify-' + ms);
          }, ms));
        });
      }
    }

    window.MILANO_schedulePDPPriceSync = schedulePDPPriceSync;
    window.MILANO_syncPDPPrice = function(reason) {
      schedulePDPPriceSync(reason || 'external', { delay: 40, verify: true });
    };


    function runPriceSync() {
      if (!syncRunning) return;
      var p = doSyncFast();
      var curDisplay = p ? p.displayText : '';
      if (curDisplay && curDisplay !== lastObservedDisplay) {
        lastObservedDisplay = curDisplay;
        doSyncBNPL(p);
      }
    }

    function scheduleSyncBurst(durationMs) {
      if (!syncRunning) return;
      burstUntil = Math.max(burstUntil, Date.now() + (durationMs || 1200));
      if (burstTimer) return;

      function tick() {
        if (!syncRunning) { burstTimer = null; return; }
        try { runPriceSync(); } catch (e) { log('[Milano] Burst sync skipped; DOM transitioning', e); }
        if (Date.now() < burstUntil) {
          burstTimer = setTimeout(tick, 250);
        } else {
          burstTimer = null;
        }
      }

      tick();
    }

    function startHealthCheck() {
      if (healthInterval) return;
      healthInterval = setInterval(function() {
        if (!syncRunning || document.hidden) return;
        try { runPriceSync(); } catch (e) { log('[Milano] Health sync skipped; DOM transitioning', e); }
      }, 1500);
    }

    // ===================================================================
    // OBSERVERS — Watch SC Options + DN for changes
    //
    // v2026-05-07a: DN observer simplified — removed weAreWritingDN guard
    // (we no longer write to DN) and resetDNVariantBase() call (removed).
    // Observer now only invalidates cache and triggers cooldown.
    // ===================================================================

    function observeSources() {
      var bnplDebounce = null;
      function onPriceChange() {
        invalidateDNCache();
        schedulePDPPriceSync('source-change', { delay: 70, verify: true });
        scheduleSyncBurst(900);
        clearTimeout(bnplDebounce);
        bnplDebounce = setTimeout(function() { schedulePDPPriceSync('source-change:bnpl', { delay: 0, verify: false }); }, 180);
      }

      // Direct SC Options change listener. This catches always-visible Cap Size
      // immediately, even before SC Options updates its rendered total node.
      if (!window._mwSCOptionsPriceChangeHooked) {
        window._mwSCOptionsPriceChangeHooked = true;
        document.addEventListener('change', function(e) {
          if (!e.target || !e.target.closest || !e.target.closest('.bold_option')) return;
          onPriceChange();
        }, true);
      }

      // Native variant and app lifecycle events. These catch Palo Alto price
      // block replacements immediately instead of waiting for the 1s health
      // check, which keeps Splitit + selected-options context stable during
      // fast Length/Lace changes and mobile stress taps.
      if (!window._mwPDPPriceEventHooks) {
        window._mwPDPPriceEventHooks = true;
        var eventSync = function() { invalidateDNCache(); schedulePDPPriceSync('variant-event', { delay: 90, verify: true }); scheduleSyncBurst(900); };
        document.addEventListener('theme:product-info:loaded', eventSync);
        document.addEventListener('milano:variant-selects-replaced', eventSync);
        document.addEventListener('milano:length-media-filtered', eventSync);
        document.addEventListener('milano:promotion-display:confirmed', eventSync);
        document.addEventListener('milano:promotion-display:cleared', eventSync);
        document.addEventListener('shopify:section:load', eventSync);
        if (typeof window.subscribe === 'function' && window.theme && window.theme.PUB_SUB_EVENTS) {
          try {
            window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, eventSync);
            window.subscribe(window.theme.PUB_SUB_EVENTS.optionValueSelectionChange, eventSync);
          } catch (_) { /* theme pub/sub unavailable */ }
        }
      }

      // Watch SC Options price element
      var scEl = findSCOptionsEl();
      if (scEl) {
        var obs1 = new MutationObserver(onPriceChange);
        obs1.observe(scEl, { childList: true, characterData: true, subtree: true });
        state.observers.push(obs1);
        if (scEl.parentElement) {
          var obs2 = new MutationObserver(onPriceChange);
          obs2.observe(scEl.parentElement, { childList: true, subtree: true });
          state.observers.push(obs2);
        }
      }

      // Watch DN container for re-renders (variant change triggers full replace)
      var dnContainer = document.querySelector('[data-la-dn-price] [data-price-wrapper]')
                     || document.querySelector('[data-la-dn-price]')
                     || document.querySelector('.limoniapps-discountninja-productprice-dynamic');
      if (dnContainer) {
        var dnDebounce = null;
        var obs3 = new MutationObserver(function() {
          // DN can mutate on every variant even with no visible promo. Only do
          // sale-specific cooldown/sync work when a real enabled campaign or
          // DN-rendered sale price is present. Native price/SC Options changes
          // are already handled by the product-info and price-wrapper observers.
          invalidateDNCache();
          var activeSale = readMilanoSaleConfig() || readDNRenderedSaleInfo();
          if (!activeSale || !(activeSale.discountCents > 0 || activeSale.percentOff > 0)) return;

          dnCooldownUntil = Date.now() + 400;
          clearTimeout(dnDebounce);
          dnDebounce = setTimeout(function() {
            dnCooldownUntil = 0;
            invalidateDNCache();
            onPriceChange();
          }, 400);
        });
        obs3.observe(dnContainer, { childList: true, subtree: true });
        state.observers.push(obs3);
      }

      // Watch the data-price-wrapper in case Palo Alto replaces the whole block
      var priceWrapper = document.querySelector('[data-price-wrapper]:not([data-atc-button])');
      if (priceWrapper && priceWrapper !== (scEl && scEl.parentElement)) {
        var obs4 = new MutationObserver(onPriceChange);
        obs4.observe(priceWrapper, { childList: true, subtree: true });
        state.observers.push(obs4);
      }

      // Product-info scoped replacement observer. Palo Alto can replace Price,
      // variant selectors, or app-injected nodes during native variant updates.
      // We filter mutations to pricing/options-related areas and debounce through
      // onPriceChange so this stays light even during rapid consecutive taps.
      var productInfoRoot = document.querySelector('product-info');
      if (productInfoRoot) {
        var rootDebounce = null;
        var relevantSelector = '[data-price-wrapper], .product__price-wrapper, .product__price-and-badge, variant-selects, .bold_options, .bold_option, .limoniapps-discountninja-productprice-dynamic, [data-la-dn-price]';
        var obs5 = new MutationObserver(function(mutations) {
          var relevant = false;
          for (var i = 0; i < mutations.length && !relevant; i++) {
            var target = mutations[i].target && mutations[i].target.nodeType === 1 ? mutations[i].target : mutations[i].target.parentElement;
            if (target && target.closest && target.closest(relevantSelector)) relevant = true;
            var added = mutations[i].addedNodes || [];
            for (var a = 0; a < added.length && !relevant; a++) {
              var node = added[a];
              if (node.nodeType === 1 && ((node.matches && node.matches(relevantSelector)) || (node.querySelector && node.querySelector(relevantSelector)))) relevant = true;
            }
          }
          if (!relevant) return;
          clearTimeout(rootDebounce);
          rootDebounce = setTimeout(onPriceChange, 60);
        });
        obs5.observe(productInfoRoot, { childList: true, subtree: true, characterData: true });
        state.observers.push(obs5);
      }
    }

    // ===================================================================
    // START
    // ===================================================================

    if (_syncStarted) return;
    _syncStarted = true;
    log('[Milano] Price sync starting (DN sale bridge + SC Options)');

    // Hide ATC button price — price shown on product form, cart bar, and
    // wizard review card. Eliminates the DN vs SC Options price conflict on
    // the ATC button entirely. Scoped to PDP only (syncATCPrice only runs on PDP).
    if (!document.getElementById('mw-atc-price-hide')) {
      var hideStyle = document.createElement('style');
      hideStyle.id = 'mw-atc-price-hide';
      hideStyle.textContent =
        '[data-price-wrapper][data-atc-button] [data-product-price],' +
        'button[name="add"] [data-product-price],' +
        '.bold_clone[type="submit"] [data-product-price] {' +
          'display:none !important' +
        '}' +
        '.bold_option_total{display:none!important}';
 
      document.head.appendChild(hideStyle);
    }

    observeSources();
    schedulePDPPriceSync('initial', { delay: 40, verify: true });
    scheduleSyncBurst(1200);
    startHealthCheck();

    // bfcache (back/forward): on a restored page no load/variant/section events fire,
    // so the price would sit on its frozen value until the next 1.5s health tick.
    // Re-sync immediately, invalidate the DN cache, and reset the one-shot flag so
    // milano:price-ready re-fires (the wizard re-renders its recap on it).
    if (!window._mwBfcacheHooked) {
      window._mwBfcacheHooked = true;
      window.addEventListener('pageshow', function (e) {
        if (!e || !e.persisted) return;
        _milanoPriceReadyFired = false;
        invalidateDNCache();
        scheduleSyncBurst(1200);
      });
    }

    state._stopPriceSync = function() {
      syncRunning = false;
      if (healthInterval) clearInterval(healthInterval);
      if (burstTimer) clearTimeout(burstTimer);
      if (scheduledPriceSyncTimer) clearTimeout(scheduledPriceSyncTimer);
      clearScheduledVerifications();
      healthInterval = null;
      burstTimer = null;
    };
  }

  // =========================================================================
  // START
  // =========================================================================

  if (isPDP) {
    // Start price / BNPL sync as soon as the PDP DOM is ready so Splitit is
    // present under the product price on first load. The 3.5s call remains as
    // a guarded fallback for slower app/theme renders (_syncStarted prevents
    // duplicate RAF loops).
    function startPriceSyncSoon() {
      setTimeout(syncATCPrice, 250);
      setTimeout(syncATCPrice, 3500);
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', startPriceSyncSoon, { once: true });
    } else {
      startPriceSyncSoon();
    }

    waitForBoldOptions(function() {
      buildPanel();
      setTimeout(function() {
        if (!state.panelBuilt) buildPanel();
        syncATCPrice();
      }, 2000);
    });

    // =====================================================================
    // ATC SAFETY NET — Bold Clone fallback
    // =====================================================================
    (function() {
      var safetyTimer = null;
      var safetyActive = false;

      function clearSafety() {
        if (safetyTimer) {
          clearTimeout(safetyTimer);
          safetyTimer = null;
        }
        safetyActive = false;
      }

      document.addEventListener('theme:cart:added', clearSafety);

      document.addEventListener('click', function(e) {
        var btn = e.target.closest('button.bold_clone[type="submit"]');
        if (!btn || safetyActive) return;

        if (btn.classList.contains('is-loading') || btn.classList.contains('is-success')) return;

        safetyActive = true;
        safetyTimer = setTimeout(function() {
          if (document.body.classList.contains('js-drawer-open-cart')) {
            clearSafety();
            return;
          }

          if (btn.classList.contains('is-loading')) {
            clearSafety();
            return;
          }

          var form = btn.closest('form[action*="/cart/add"]');
          if (form) {
            if (typeof window.MILANO_prepareProductFormForCart === 'function') {
              window.MILANO_prepareProductFormForCart(form);
            }
            console.warn('[Milano] ATC safety net activated — forcing form submit');
            var productForm = btn.closest('product-form');
            if (productForm && typeof productForm.onSubmitHandler === 'function') {
              var fakeEvent = { preventDefault: function() {} };
              try {
                productForm.onSubmitHandler(fakeEvent);
              } catch (err) {
                console.warn('[Milano] Safety net product-form submit failed, using native:', err);
                form.submit();
              }
            } else {
              form.submit();
            }
          }
          clearSafety();
        }, 3000);
      }, true);
    })();
  }


  // ===========================================================================
  // PRODUCT META RHYTHM STABILIZER — v2026-06-01d
  // ---------------------------------------------------------------------------
  // Keeps the price/badge/title stack visually stable after Palo Alto replaces
  // product-info blocks on native variant changes. This is intentionally light:
  // no polling and no broad observers. It only reads the current rendered state
  // and adds classes that CSS can use to collapse empty inventory rows or reserve
  // a compact badge rail when Preorder/Promo is actually visible.
  // ===========================================================================
  (function() {
    if (window._mwProductMetaRhythmBooted) return;
    window._mwProductMetaRhythmBooted = true;

    var timer = null;

    function isVisible(el) {
      if (!el || !el.isConnected) return false;
      var cs = window.getComputedStyle ? window.getComputedStyle(el) : null;
      if (cs && (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity || '1') === 0)) return false;
      return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    }

    function closestBlock(el) {
      return el && (el.closest('.product__block') || el.closest('[id^="Inventory-"]') || el.parentElement);
    }

    function hasVisibleRating() {
      var selectors = [
        '.product__block--rating',
        '.okeReviews',
        '[data-oke-reviews]',
        '.oke-sr',
        '.spr-badge',
        '[data-rating]'
      ];
      for (var i = 0; i < selectors.length; i++) {
        var nodes = document.querySelectorAll(selectors[i]);
        for (var n = 0; n < nodes.length; n++) {
          var text = (nodes[n].textContent || '').trim();
          if (isVisible(nodes[n]) && text) return true;
        }
      }
      return false;
    }

    function hasVisiblePreorder(countdown) {
      if (!countdown) return false;
      var preorder = countdown.querySelector('.variant__countdown--preorder');
      var txt = (countdown.textContent || '').trim();
      return countdown.classList.contains('count-is-preorder') ||
             (preorder && isVisible(preorder) && (preorder.textContent || '').trim()) ||
             /pre\s*-?\s*order|preorder/i.test(txt);
    }

    function refreshMetaRhythm() {
      if (!document.body || !document.body.classList.contains('template-product')) return;

      var countdown = document.querySelector('.variant__countdown');
      var inventoryBlock = closestBlock(countdown);
      var hasPreorder = hasVisiblePreorder(countdown);
      var hasBadge = hasPreorder;
      var hasRating = hasVisibleRating();

      document.body.classList.toggle('mw-current-has-preorder', hasPreorder);
      document.body.classList.remove('mw-current-has-promo-badge');
      document.body.classList.toggle('mw-current-has-badge', hasBadge);
      document.body.classList.toggle('mw-current-has-rating', hasRating);
      document.body.classList.toggle('mw-compact-meta-gap', !hasBadge && !hasRating);

      document.querySelectorAll('.mw-inventory-block').forEach(function(el) {
        if (el !== inventoryBlock) {
          el.classList.remove('mw-inventory-block', 'mw-visible-inventory-state', 'mw-empty-inventory-state');
        }
      });

      if (inventoryBlock) {
        inventoryBlock.classList.add('mw-inventory-block');
        inventoryBlock.classList.toggle('mw-visible-inventory-state', hasBadge);
        inventoryBlock.classList.toggle('mw-empty-inventory-state', !hasBadge);
      }
    }

    function schedule(reason) {
      if (timer) clearTimeout(timer);
      requestAnimationFrame(function() {
        timer = setTimeout(function() {
          timer = null;
          try { refreshMetaRhythm(); } catch (_) { /* non-critical visual state */ }
        }, reason === 'variant' ? 60 : 20);
      });
    }

    window.MILANO_refreshProductMetaRhythm = function() { schedule('external'); };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() { schedule('dom'); }, { once: true });
    } else {
      schedule('initial');
    }

    document.addEventListener('milano:variant-selects-replaced', function() { schedule('variant'); });
    document.addEventListener('theme:product-info:loaded', function() { schedule('variant'); });
    document.addEventListener('shopify:section:load', function() { schedule('section'); });
    document.addEventListener('change', function(e) {
      if (!e.target || !e.target.matches || !e.target.matches('input[data-single-option-selector], select[data-single-option-selector]')) return;
      schedule('variant');
    }, true);
    if (typeof window.subscribe === 'function' && window.theme && window.theme.PUB_SUB_EVENTS) {
      try {
        window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, function() { schedule('variant'); });
      } catch (_) { /* pub/sub optional */ }
    }
  })();

})();
